<template>
<div class="pl-root" :class="{ 'pl-selecting': selecting }">

    <!-- ============ app bar ============ -->
    <div class="pl-appbar">
        <div class="pl-brand"><span class="pl-livedot"></span>Planner</div>

        <div class="pl-zoom">
            <!-- Day/Week/Month are planning surfaces (people, tray, lane) —
                 the shop-floor tier gets the Checklist view only -->
            <template v-if="permissions.full_board">
                <button type="button" :class="{ on: zoom === 'day' }" @click="setZoom('day')">Day</button>
                <button type="button" :class="{ on: zoom === 'week' }" @click="setZoom('week')">Week</button>
                <button type="button" :class="{ on: zoom === 'month' }" @click="setZoom('month')">Month</button>
                <!-- Frances's wall boards (planning/planner-boards-p1-p3-p4-plan.md):
                     Dates = deliveries · deadlines · events; People = who holds which jobs -->
                <button type="button" :class="{ on: zoom === 'dates' }" @click="setZoom('dates')">Dates</button>
                <button type="button" :class="{ on: zoom === 'people' }" @click="setZoom('people')">People</button>
            </template>
            <button type="button" :class="{ on: zoom === 'checklist' }" @click="setZoom('checklist')">Checklist</button>
        </div>

        <div class="pl-pager" v-if="zoom !== 'checklist'">
            <button type="button" @click="page(-1)" aria-label="Previous">‹</button>
            <button type="button" class="pl-today-btn" @click="goToday">Today</button>
            <button type="button" @click="page(1)" aria-label="Next">›</button>
        </div>

        <div class="pl-range" v-if="zoom !== 'checklist'">{{ rangeLabel }}</div>

        <div class="pl-filters" v-if="zoom !== 'checklist'">
            <!-- multi-select → bulk move (planning/planner-bulk-move-design.md):
                 the toggle is the touch route in; Ctrl/Cmd-click a chip is the
                 desktop shortcut. Drag is off while picking. -->
            <button v-if="canWrite && (zoom === 'week' || zoom === 'day')" type="button"
                    class="pl-select-btn" :class="{ on: selecting }"
                    :title="selecting ? 'Stop selecting (Esc)' : 'Select several chips, then move them together — or Ctrl-click a chip'"
                    @click="selecting ? exitSelect() : enterSelect()">
                {{ selecting ? '☑ Selecting…' : '☐ Select' }}
            </button>
            <!-- range for the wall boards: one row, the calendar month week
                 by week, or four weeks from this Monday -->
            <span v-if="isBoardZoom" class="pl-range-ctl" role="group" aria-label="Range">
                <button type="button" :class="{ on: rangeMode === 'week' }" @click="setRangeMode('week')">Week</button>
                <button type="button" :class="{ on: rangeMode === 'month' }" @click="setRangeMode('month')">Month</button>
                <button type="button" :class="{ on: rangeMode === '28d' }" @click="setRangeMode('28d')">28 days</button>
            </span>
            <span class="pl-jobsearch">
                <input type="text" class="pl-filter pl-jobsearch-in" v-model="jobSearch"
                       placeholder="🔍 Find a job — name or Q number"
                       @focus="searchOpen = true" @input="searchOpen = true"
                       @keydown.enter.prevent="pickFirstMatch" @keydown.esc="searchOpen = false"
                       @blur="closeSearchSoon">
                <div v-if="searchOpen && jobMatches.length" class="pl-jobsearch-dd">
                    <button v-for="m in jobMatches" :key="'js' + m.id" type="button"
                            @mousedown.prevent="pickJob(m)">
                        <b>Q{{ m.id }}</b> · {{ m.name }}
                    </button>
                </div>
            </span>
            <select v-if="!isBoardZoom" v-model="wcFilter" class="pl-filter">
                <option :value="null">Workcentre: all</option>
                <option v-for="wc in board.workcentres" :key="wc.id" :value="wc.id">{{ wc.name }}</option>
            </select>
            <!-- P4 flag filters — "show me what's waiting on artwork" -->
            <button type="button" class="pl-flagbtn" :class="{ on: flagFilter === 'artwork_waiting' }"
                    title="Only jobs waiting on artwork" @click="toggleFlagFilter('artwork_waiting')">⏳ artwork</button>
            <button type="button" class="pl-flagbtn" :class="{ on: flagFilter === 'priority' }"
                    title="Only priority jobs" @click="toggleFlagFilter('priority')">★ priority</button>
            <button v-if="quoteFilter || wcFilter || flagFilter" type="button" class="pl-filter-clear" @click="quoteFilter = null; wcFilter = null; flagFilter = null">✕ clear</button>

            <span v-if="quoteFilter && permissions.can_replan" class="pl-shiftctl" title="Move this quote's unfinished slots by N days — people and order kept">
                <input type="number" v-model.number="shiftDays" min="-30" max="30" class="pl-shift-days">
                <button type="button" :disabled="!shiftDays" @click="doShift">⇄ Shift remaining</button>
            </span>

            <button v-if="quoteFilter && permissions.can_replan" type="button" class="pl-adhoc-btn"
                    :disabled="pullBusy" @click="pullToday"
                    title="Gather this job's unfinished work onto today (people kept, same-person days merged) — then assign it out from the Day view">
                {{ pullBusy ? 'Pulling…' : '⇩ Pull all to today' }}
            </button>

            <span v-if="quoteFilter && permissions.can_replan" class="pl-shiftctl"
                  title="The date the client expects the job — sets it on every calendar card, drives the lane colours and OTIF">
                <span class="pl-ddl-flag">⚑</span>
                <input type="date" v-model="deadlineDate" class="pl-ddl-date">
                <button type="button" :disabled="deadlineSaving || !deadlineDate || !deadlineChanged" @click="saveDeadline">
                    {{ deadlineSaving ? 'Saving…' : 'Set deadline' }}
                </button>
            </span>

            <button v-if="permissions.can_replan" type="button" class="pl-adhoc-btn" @click="adhocOpen = !adhocOpen">
                ＋ Ad-hoc
            </button>
        </div>

        <div v-if="adhocOpen && zoom !== 'checklist'" class="pl-adhoc-form">
            <select v-model="adhoc.type" class="pl-filter">
                <option value="adhoc">Ad-hoc (maintenance, unit work…)</option>
                <option value="delivery">Delivery run</option>
            </select>
            <input v-model="adhoc.title" class="pl-filter pl-adhoc-title" placeholder="What is it? e.g. Service the Morso">
            <select v-model.number="adhoc.userId" class="pl-filter">
                <option :value="null">Person…</option>
                <option v-for="pn in board.people" :key="'ah' + pn.id" :value="pn.id">{{ pn.name }}</option>
            </select>
            <input type="date" v-model="adhoc.date" class="pl-filter">
            <input type="number" v-model.number="adhoc.minutes" min="5" max="600" step="5" class="pl-filter pl-adhoc-mins" title="minutes">
            <button type="button" class="pl-adhoc-add" :disabled="!adhoc.title || !adhoc.userId || !adhoc.date || !adhoc.minutes" @click="addTyped">Add to the plan</button>
            <button type="button" class="pl-filter-clear" @click="adhocOpen = false">cancel</button>
        </div>

        <div v-if="quoteFilter && permissions.can_replan && zoom !== 'checklist'" class="pl-feed-form"
             title="Feed a big background order onto the board at N units/week into the spare capacity the deadline jobs leave — never over-fills anyone.">
            <span class="pl-feed-lbl"><svg class="pl-i"><use href="#pl-zap"/></svg> Trickle-feed Q{{ quoteFilter }}</span>
            <input type="number" v-model.number="feedRate" min="1" max="50" class="pl-filter pl-feed-rate" placeholder="N">
            <span class="pl-feed-unit">units / week</span>
            <button type="button" class="pl-filter-clear" :disabled="feedSaving" @click="saveFeedRate">{{ feedSaving ? 'Saving…' : 'Set rate' }}</button>
            <button type="button" class="pl-feed-go" :disabled="!savedFeedRate || feedDistributing" @click="distributeFeed">
                {{ feedDistributing ? 'Distributing… (~1–2 min)' : '▸ Distribute now' }}
            </button>
            <button v-if="savedFeedRate" type="button" class="pl-filter-clear" @click="clearFeedRate">stop feeding</button>
        </div>
    </div>

    <!-- ============ selection bar ============ -->
    <div v-if="selecting && (zoom === 'week' || zoom === 'day')" class="pl-selbar">
        <span v-if="selectedCount" class="pl-selbar-n">
            <b>{{ selectedCount }}</b> selected · {{ secs(selectedSeconds) }} · {{ selectedJobs }} job{{ selectedJobs === 1 ? '' : 's' }}
            <template v-if="selectedOffView"> · <span class="pl-selbar-off">{{ selectedOffView }} not in this view</span></template>
        </span>
        <span v-else class="pl-selbar-n pl-selbar-hint">Click chips to select them — drag is off while you pick. Esc to stop.</span>
        <button v-if="lastPickedQuote && jobChipsToAdd" type="button" class="pl-selbar-add"
                :title="'Add every movable chip of this job that is on the board right now'"
                @click="selectJobInView(lastPickedQuote)">
            ＋ all of Q{{ lastPickedQuote }} in view ({{ jobChipsToAdd }})
        </button>
        <span class="pl-selbar-sp"></span>
        <button type="button" class="pl-selbar-move" :disabled="!selectedCount" @click="bulkOpen = true">
            Move{{ selectedCount ? ' ' + selectedCount : '' }}…
        </button>
        <button type="button" class="pl-selbar-btn" :disabled="!selectedCount" @click="clearSelection">Clear</button>
        <button type="button" class="pl-selbar-btn" @click="exitSelect">Done</button>
    </div>

    <div v-if="error" class="pl-error">
        {{ error }} <button type="button" @click="error = null">✕</button>
    </div>

    <div v-if="loading && !board.dates.length" class="pl-loading">Loading the board…</div>

    <template v-if="board.dates.length">

        <!-- ============ searched-job progress panel ============ -->
        <job-process-panel v-if="quoteFilter && !assignMode && permissions.full_board && zoom !== 'checklist'"
                           :quote-id="quoteFilter" :board="board" :permissions="permissions"
                           :refresh-key="panelRefresh"
                           @open-chip="openDrawer"
                           @assign="enterAssign(quoteFilter)"
                           @changed="fetchBoard"
                           @removed="onJobRemoved"
                           @restored="onJobRestored"
                           @error="error = $event" />

        <!-- ============ Checklist zoom — all jobs in flight ============ -->
        <checklist-view v-if="zoom === 'checklist'"
                        :board="board" :permissions="permissions" :refresh-key="panelRefresh"
                        @open-chip="openDrawer"
                        @refetch="fetchBoard"
                        @error="error = $event" />

        <!-- ============ assign mode banner + tiles shelf ============ -->
        <template v-if="assignMode && zoom === 'week'">
            <div class="pl-assign-banner">
                <div>
                    <div class="pl-assign-t">Assign mode — Q{{ quoteFilter }} · {{ assignQuoteName }}</div>
                    <div class="pl-assign-sub">drag a tile onto a day, or let Auto pick the first free slot in that workcentre</div>
                </div>
                <div class="pl-assign-prog" v-if="assignTotals.total">
                    <span>{{ assignTotals.placed }} of {{ assignTotals.total }} placed</span>
                    <span class="pl-assign-bar"><i :style="{ width: (assignTotals.placed / assignTotals.total * 100) + '%' }"></i></span>
                </div>
                <button type="button" class="pl-assign-done" @click="exitAssign">Done</button>
            </div>

            <div class="pl-shelf" v-if="assignGroup && assignGroup.processes.length">
                <draggable class="pl-tiles"
                           :list="assignGroup.processes"
                           :group="{ name: 'slots', pull: (canWrite && !selecting) ? 'clone' : false, put: false }"
                           :sort="false"
                           v-bind="dragOpts"
                           :move="onMoveCheck"
                           @start="onDragStart($event, assignGroup.processes)"
                           @end="onDragEnd">
                    <div v-for="proc in assignGroup.processes" :key="'tile' + proc.checklist_id"
                         class="pl-tile" :class="{ 'pl-no-drag': !elementMovable(proc) }" :title="procTitle(proc)"
                         :style="{ '--plwc': wcColour(proc.workcentre_id) }">
                        <svg class="pl-i pl-tile-i"><use :href="'#' + wcIcon(proc.workcentre_id)"/></svg>
                        <div class="pl-tile-tx">
                            <div class="pl-tile-nm">{{ proc.pretty_name }}</div>
                            <div v-if="proc.item_name && multiItemGroup(assignGroup)" class="pl-tile-item">{{ proc.item_name }}</div>
                            <div class="pl-tile-meta">{{ wcName(proc.workcentre_id) || 'Misc' }} · {{ mins(proc.est_minutes) }} · {{ proc.num_people }} {{ proc.num_people > 1 ? 'people' : 'person' }}</div>
                        </div>
                        <button v-if="canWrite" type="button" class="pl-tile-auto" :disabled="autoBusy === proc.checklist_id" @click="autoPlace(proc)">
                            <svg class="pl-i"><use href="#pl-zap"/></svg>{{ autoBusy === proc.checklist_id ? '…' : 'Auto' }}
                        </button>
                    </div>
                </draggable>
            </div>
            <div class="pl-shelf pl-shelf-empty" v-else>
                Everything on Q{{ quoteFilter }} is placed ✓ — review below, then Done.
            </div>
        </template>

        <!-- ============ jobs overview lane ============ -->
        <div class="pl-lane" v-if="zoom !== 'day' && zoom !== 'checklist' && !isBoardZoom && visibleJobs.length">
            <div class="pl-lane-head">
                <h2>Jobs in flight</h2>
                <span class="pl-lane-count">{{ visibleJobs.length }} jobs · {{ money(totalJobsValue) }}</span>
                <div class="pl-lane-legend">
                    <span><i style="background:var(--pl-good)"></i>On plan</span>
                    <span><i style="background:var(--pl-warn)"></i>Tight</span>
                    <span><i style="background:var(--pl-crit)"></i>Past deadline</span>
                    <span><i style="background:var(--pl-ink3)"></i>Trickle feed</span>
                    <span>⚑ deadline</span>
                </div>
                <button type="button" class="pl-lane-fold" @click="laneOpen = !laneOpen">{{ laneOpen ? '▾ Collapse' : '▸ Expand' }}</button>
            </div>
            <div class="pl-lane-rows" v-show="laneOpen">
                <div class="pl-lane-row" v-for="job in visibleJobs" :key="job.quote_id"
                     :class="{ 'pl-lane-row-active': quoteFilter === job.quote_id }">
                    <div class="pl-lane-lbl">
                        <a v-if="job.first_item_id" class="pl-lane-sheet" :href="sheetUrl(job.first_item_id)"
                           data-ajax="false" title="Open the job sheet — view & scribble" @click.stop>📄</a>
                        <b>Q{{ job.quote_id }}</b> · {{ job.quote_name }}
                        <flag-pills :flags="job.flags" compact :show-pre="false" />
                    </div>
                    <div class="pl-lane-track">
                        <span v-for="i in (board.dates.length - 1)" :key="i" class="pl-lane-gridl" :style="{ left: (i / board.dates.length * 100) + '%' }"></span>
                        <div class="pl-lane-bar"
                             :class="['pl-risk-' + (job.feed_units_per_week ? 'feed' : job.risk), { 'pl-lane-ghost': !job.released || job.unplanned, 'pl-cont-l': job.clipL, 'pl-cont-r': job.clipR }]"
                             :style="{ left: job.left + '%', width: job.width + '%' }"
                             :title="quoteFilter === job.quote_id ? 'Show all jobs' : 'Show only this job\'s processes'"
                             @click="toggleLaneFilter(job.quote_id)">
                            <span class="pl-lane-fill" :style="{ width: job.pct_complete + '%' }"></span>
                            <span v-if="job.flagLeft !== null" class="pl-lane-flag" :style="{ left: job.flagLeft + '%' }" :title="'Deadline ' + job.deadline"></span>
                            <span class="pl-lane-pct">{{ job.pct_complete }}%<template v-if="job.feed_units_per_week"> · trickle {{ job.feed_units_per_week }}/wk</template><template v-if="job.unplanned"> · not on the board yet</template></span>
                        </div>
                    </div>
                    <div class="pl-lane-val">{{ money(job.value_pence) }}</div>
                </div>
            </div>
        </div>

        <!-- ============ board: tray + grid (Week zoom) ============ -->
        <div class="pl-board" v-if="zoom === 'week'">

            <aside class="pl-tray" v-if="visibleTray.length || quoteFilter">
                <h2><svg class="pl-i"><use href="#pl-tray"/></svg>Unscheduled</h2>
                <div class="pl-tray-sub">Processes with no person + day yet — drag onto the grid</div>

                <div class="pl-tray-q" v-for="group in visibleTray" :key="group.quote_id">
                    <div class="pl-tray-qh">
                        <b>Q{{ group.quote_id }} · {{ group.quote_name }}</b>
                        <span>{{ group.processes.length }} left</span>
                    </div>
                    <draggable class="pl-tray-list"
                               :list="group.processes"
                               :group="{ name: 'slots', pull: (canWrite && !selecting) ? 'clone' : false, put: false }"
                               :sort="false"
                               v-bind="dragOpts"
                               :move="onMoveCheck"
                               @start="onDragStart($event, group.processes)"
                               @end="onDragEnd">
                        <div v-for="proc in group.processes" :key="'tray' + proc.checklist_id"
                             class="pl-chip"
                             :class="{ 'pl-no-drag': !elementMovable(proc) }"
                             :title="procTitle(proc)"
                             :style="chipStyle(proc.workcentre_id)">
                            <svg class="pl-i"><use :href="'#' + wcIcon(proc.workcentre_id)"/></svg>
                            <div class="pl-chip-cx">
                                <div class="pl-chip-r1"><span class="pl-chip-nm">{{ proc.pretty_name }}</span><span class="pl-chip-mt">{{ mins(proc.est_minutes) }}</span></div>
                                <!-- which unit: a multi-item order's processes all read the
                                     same, so the item name (with the client's ref code —
                                     "4ft circle ref 2K9AL…") is what makes them assignable -->
                                <div class="pl-chip-r2" v-if="proc.late_add || (proc.item_name && multiItemGroup(group))">
                                    <span v-if="proc.item_name && multiItemGroup(group)" class="pl-chip-item">{{ trayItemLabel(proc) }}</span>
                                    <span v-if="proc.late_add" class="pl-pill pl-pill-seq">late add</span>
                                </div>
                            </div>
                        </div>
                    </draggable>
                    <button v-if="canWrite && !assignMode" type="button" class="pl-tray-go" @click="enterAssign(group.quote_id)">
                        Assign on the planner →
                    </button>
                </div>
            </aside>

            <div class="pl-gridwrap">
                <div class="pl-grid" :style="gridColumns">
                    <div class="pl-hd pl-hd-people">People</div>
                    <div v-for="date in board.dates" :key="'hd' + date"
                         class="pl-hd" :class="{ 'pl-hd-today': date === board.today, 'pl-hd-weekend': isWeekend(date) }">
                        {{ dayName(date) }} <span class="pl-hd-num">{{ dayNum(date) }}</span>
                        <span v-if="date === board.today" class="pl-today-pill">Today</span>
                    </div>
                    <div class="pl-hd">Week</div>

                    <template v-for="person in board.people">
                        <div class="pl-person" :key="'p' + person.id" :class="{ 'pl-row-dim': rowDimmed(person), 'pl-row-target': dragTargetRow === person.id }">
                            <span class="pl-av">{{ initials(person.name) }}</span>
                            <div class="pl-person-tx">
                                <div class="pl-person-nm">{{ person.name }}</div>
                            </div>
                            <div v-if="person.auto_fill_percent !== null || permissions.can_edit_limits" class="pl-limit-wrap">
                                <button type="button" class="pl-limit-pill"
                                        :disabled="!permissions.can_edit_limits"
                                        title="Auto placement fills this person's days to this ceiling at most — manual drags are unaffected"
                                        @click="openLimitEditor(person)">
                                    <svg class="pl-i"><use href="#pl-zap"/></svg>
                                    Auto ≤ {{ person.auto_fill_percent === null ? 100 : person.auto_fill_percent }}%
                                    <svg v-if="permissions.can_edit_limits" class="pl-i pl-pen"><use href="#pl-edit"/></svg>
                                </button>

                                <div v-if="limitEditor.userId === person.id" class="pl-limpop">
                                    <h5>Auto-fill limit — {{ person.name }}</h5>
                                    <div class="pl-limpop-step">
                                        <button type="button" @click="limitEditor.percent = Math.max(5, limitEditor.percent - 5)">−</button>
                                        <b>{{ limitEditor.percent }}%</b>
                                        <button type="button" @click="limitEditor.percent = Math.min(150, limitEditor.percent + 5)">+</button>
                                    </div>
                                    <p>Auto placement fills {{ person.name }}'s days to {{ limitEditor.percent }}% at most. Manual drags are unaffected.</p>
                                    <div class="pl-limpop-btns">
                                        <button type="button" class="pl-limpop-save" @click="saveLimit(person)" :disabled="limitEditor.saving">
                                            {{ limitEditor.saving ? 'Saving…' : 'Save' }}
                                        </button>
                                        <button type="button" class="pl-limpop-cancel" @click="limitEditor.userId = null">Cancel</button>
                                    </div>
                                </div>
                            </div>

                            <div v-if="permissions.can_replan" class="pl-limit-wrap">
                                <button type="button" class="pl-limit-pill"
                                        title="Set this person's hours for a date range — 0 hours = off (holiday / sick)"
                                        @click="openHoursEditor(person)">
                                    Hours / off
                                    <svg class="pl-i pl-pen"><use href="#pl-edit"/></svg>
                                </button>

                                <div v-if="hoursEditor.userId === person.id" class="pl-limpop">
                                    <h5>Hours — {{ person.name }}</h5>
                                    <div class="pl-hrs-row"><label>From</label><input type="date" v-model="hoursEditor.from"></div>
                                    <div class="pl-hrs-row"><label>To</label><input type="date" v-model="hoursEditor.to"></div>
                                    <div class="pl-hrs-row">
                                        <label>Hours</label>
                                        <input type="number" min="0" max="24" step="0.5" v-model.number="hoursEditor.hours">
                                        <button type="button" class="pl-hrs-off" @click="hoursEditor.hours = 0">Off</button>
                                    </div>
                                    <p>0 hours = off (holiday / sick). The boards and auto placement follow this straight away.</p>
                                    <div class="pl-limpop-btns">
                                        <button type="button" class="pl-limpop-save" @click="saveHours(person, false)" :disabled="hoursEditor.saving">
                                            {{ hoursEditor.saving ? 'Saving…' : 'Save' }}
                                        </button>
                                        <button type="button" class="pl-limpop-cancel" @click="hoursEditor.userId = null">Cancel</button>
                                    </div>
                                    <button type="button" class="pl-hrs-clear" @click="saveHours(person, true)" :disabled="hoursEditor.saving">
                                        Clear — back to usual pattern
                                    </button>
                                </div>
                            </div>

                            <div v-if="person.feed_url" class="pl-limit-wrap">
                                <button type="button" class="pl-limit-pill"
                                        :title="'Copy ' + person.name + '’s calendar feed link — paste into their phone or Google Calendar (Add calendar → From URL)'"
                                        @click="copyPersonFeed(person)">
                                    📅 {{ feedCopiedId === person.id ? '✓ Copied' : 'Calendar link' }}
                                </button>
                            </div>
                        </div>

                        <div v-for="date in board.dates" :key="'c' + person.id + date"
                             class="pl-cell"
                             :class="cellClasses(person, date)">
                            <div class="pl-cap" :class="capBand(person, date)" v-if="capacitySeconds(person, date) > 0">
                                <span class="pl-cap-track">
                                    <span class="pl-cap-fill" :style="{ width: Math.min(capPct(person, date), 100) + '%' }"></span>
                                    <span v-if="person.auto_fill_percent !== null" class="pl-cap-lim" :style="{ left: Math.min(person.auto_fill_percent, 100) + '%' }"></span>
                                </span>
                                <span class="pl-cap-n">{{ capPct(person, date) }}% <small>· {{ secs(plannedSeconds(person, date)) }}/{{ secs(capacitySeconds(person, date)) }}</small></span>
                            </div>
                            <div class="pl-cap" v-else>
                                <span class="pl-cap-n pl-cap-none">{{ isWeekend(date) ? '' : 'no set hours' }}</span>
                            </div>

                            <draggable class="pl-cell-list"
                                       :list="cellList(person.id, date)"
                                       group="slots"
                                       :disabled="!canWrite || selecting"
                                       :data-user-id="person.id"
                                       :data-date="date"
                                       v-bind="dragOpts"
                                       :move="onMoveCheck"
                                       @start="onDragStart($event, cellList(person.id, date))"
                                       @end="onDragEnd"
                                       @change="onCellChange(person.id, date, $event)">
                                <div v-for="slot in cellList(person.id, date)" :key="slot.id"
                                     v-show="chipVisible(slot)"
                                     class="pl-chip"
                                     :class="[{
                                         'pl-chip-done': slot.checked,
                                         'pl-chip-ghost': !slot.released && !slot.checked,
                                         'pl-chip-typed': slot.type !== 'process',
                                         'pl-chip-sib': hoverQuote && slot.quote_id === hoverQuote,
                                         'pl-chip-sel': !!selected[slot.id],
                                         'pl-chip-unsel': selecting && !slotMovable(slot),
                                         'pl-no-drag': !slotMovable(slot),
                                     }]"
                                     :style="chipStyle(slot.workcentre_id, slot.type)"
                                     :title="slot.pretty_name + (slot.quote_id ? ' — Q' + slot.quote_id + ' · ' + (slot.quote_name || '') : '')"
                                     @mouseenter="hoverQuote = slot.quote_id"
                                     @mouseleave="hoverQuote = null"
                                     @click="chipClick(slot, $event)">
                                    <span v-if="selecting && slotMovable(slot)" class="pl-chip-ck" aria-hidden="true">{{ selected[slot.id] ? '✓' : '' }}</span>
                                    <span v-else-if="chipMark(slot)" class="pl-chip-flag" :title="chipMarkTitle(slot)">{{ chipMark(slot) }}</span>
                                    <svg class="pl-i"><use :href="'#' + wcIcon(slot.workcentre_id, slot.type)"/></svg>
                                    <div class="pl-chip-cx">
                                        <div class="pl-chip-r1">
                                            <span class="pl-chip-nm">{{ slot.checked ? '✓ ' : '' }}{{ slot.pretty_name }}</span>
                                            <span class="pl-chip-mt">{{ secs(slot.planned_seconds) }}</span>
                                        </div>
                                        <div class="pl-chip-r2">
                                            <span class="pl-chip-cq">{{ slot.quote_id ? ('Q' + slot.quote_id + ' · ' + (slot.quote_name || '')) : typeLabel(slot.type) }}</span>
                                            <span v-if="slot.is_draft" class="pl-pill pl-pill-draft">Draft</span>
                                        </div>
                                    </div>
                                </div>
                            </draggable>

                            <div v-if="dragging && dropReason(person, date)" class="pl-cell-why">
                                <svg class="pl-i"><use href="#pl-lock"/></svg>{{ dropReason(person, date) }}
                            </div>
                        </div>

                        <div class="pl-wktotal" :key="'t' + person.id">
                            <template v-if="weekCapacity(person) > 0">
                                <span class="pl-wktotal-t">{{ secs(weekPlanned(person)) }} <small>/ {{ secs(weekCapacity(person)) }}</small></span>
                                <div class="pl-cap" :class="bandClass(weekPct(person))">
                                    <span class="pl-cap-track">
                                        <span class="pl-cap-fill" :style="{ width: Math.min(weekPct(person), 100) + '%' }"></span>
                                        <span v-if="person.auto_fill_percent !== null" class="pl-cap-lim" :style="{ left: Math.min(person.auto_fill_percent, 100) + '%' }"></span>
                                    </span>
                                    <span class="pl-cap-n">{{ weekPct(person) }}%</span>
                                </div>
                            </template>
                            <span v-else class="pl-wktotal-t pl-cap-none">—</span>
                        </div>
                    </template>
                </div>
            </div>
        </div>

        <!-- ============ Day zoom ============ -->
        <day-view v-if="zoom === 'day'"
                  :board="board" :permissions="permissions"
                  :selecting="selecting" :selected-ids="selected"
                  @pick="chipClick"
                  @open-drawer="openDrawer"
                  @refetch="fetchBoard"
                  @dragstate="onChildDrag"
                  @error="error = $event"
                  @moved="onDayMoved"
                  @assign-quote="enterAssign" />

        <!-- ============ Month zoom ============ -->
        <month-view v-if="zoom === 'month'" :board="board" @goto-day="gotoDay" />

        <!-- ============ the wall boards ============ -->
        <people-board v-if="zoom === 'people'"
                      :start="boardRange.start" :weeks="boardRange.weeks" :range-mode="rangeMode"
                      :board="board" :permissions="permissions" :flag-filter="flagFilter"
                      :refresh-key="panelRefresh"
                      @bulk-move="onBoardMove"
                      @open-job="openJob"
                      @error="error = $event" />

        <dates-board v-if="zoom === 'dates'"
                     :start="boardRange.start" :weeks="boardRange.weeks" :range-mode="rangeMode"
                     :board="board" :permissions="permissions" :flag-filter="flagFilter"
                     :refresh-key="panelRefresh"
                     @set-deadline="onSetDeadline"
                     @open-job="openJob"
                     @changed="fetchBoard"
                     @toast="showToast($event)"
                     @error="error = $event" />
    </template>

    <chip-drawer v-if="drawerChip"
                 :chip="drawerChip" :board="board" :permissions="permissions"
                 @close="drawerChip = null"
                 @changed="fetchBoard" />

    <!-- bulk move: the selection's side panel — one target for all, a
         live dry-run preview, and an exact-undo receipt on the way out -->
    <bulk-move-drawer v-if="bulkOpen"
                      :chips="selectedList" :board="board" :permissions="permissions"
                      :busy="bulkBusy" :move-error="bulkError" :preset="bulkPreset"
                      @close="closeBulk"
                      @remove="unselect"
                      @move="doBulkMove" />

    <!-- drop receipt: every drag announces where the work landed, with
         one-click undo — silent mis-drops were how patina "vanished" -->
    <div v-if="toast" class="pl-toast">
        <span>{{ toast.msg }}</span>
        <button v-if="toast.done" type="button" class="pl-toast-undo pl-toast-done" @click="runDone">✓ {{ toast.doneLabel || 'Mark done' }}</button>
        <button v-if="toast.undo" type="button" class="pl-toast-undo" @click="runUndo">↩ Undo</button>
        <button type="button" class="pl-toast-x" @click="dismissToast" aria-label="Dismiss">✕</button>
    </div>
</div>
</template>

<script>
import draggable from 'vuedraggable';
import { copyText, productionSheetUrl, DRAG_OPTS } from './shared';

const WC_COLOURS = {
    'Metal': '#2a78d6',
    'Wood': '#eb6834',
    'Spray-finish': '#4a3aa7',
    'Mount': '#eda100',
    'Glazing': '#1baf7a',
    'Fitup': '#008300',
    'Acrylic': '#e34948',
    'Print': '#e87ba4',
};
const WC_ICONS = {
    'Metal': 'pl-metal',
    'Wood': 'pl-wood',
    'Spray-finish': 'pl-spray',
    'Mount': 'pl-mount',
    'Glazing': 'pl-glazing',
    'Fitup': 'pl-fitup',
    'Acrylic': 'pl-acrylic',
    'Print': 'pl-print',
};
const NEUTRAL = '#6b6a66';

import ChipDrawer from './ChipDrawer';
import DayView from './DayView';
import MonthView from './MonthView';
import JobProcessPanel from './JobProcessPanel';
import ChecklistView from './ChecklistView';
import BulkMoveDrawer from './BulkMoveDrawer';
import FlagPills from './FlagPills';
import PeopleBoard from './PeopleBoard';
import DatesBoard from './DatesBoard';

export default {
    name: 'PlannerPage',
    components: { draggable, ChipDrawer, DayView, MonthView, JobProcessPanel, ChecklistView, BulkMoveDrawer, FlagPills, PeopleBoard, DatesBoard },

    data() {
        return {
            zoom: 'week',            // day | week | month | dates | people | checklist
            rangeMode: '28d',        // dates/people boards: week | month | 28d (planner-boards plan §1)
            flagFilter: null,        // null | artwork_waiting | priority — the P4 filter pills
            boardBusy: false,        // a board-originated move (dry run → commit) is in flight
            bulkPreset: null,        // target pre-loaded into the bulk panel by a refused board drop
            anchor: this.ymd(new Date()),     // a date inside the visible period
            board: { dates: [], people: [], slots: [], tray: [], jobs: [], workcentres: [], carryover: [], today: '', efficiency_percent: 100, feed_rates: {} },
            permissions: { can_place: false, can_replan: false, can_edit_limits: false, can_tick: false, can_manage: false, full_board: false },
            cells: {},               // "userId|date" -> mutable slot array (vuedraggable owns these)
            loading: false,
            error: null,
            laneOpen: true,
            quoteFilter: null,
            wcFilter: null,
            hoverQuote: null,
            dragging: null,          // the slot / tray process in hand
            hoverCell: null,         // "userId|date" the drag would drop into
            childDragging: false,    // DayView drag in progress
            limitEditor: { userId: null, percent: 100, saving: false },
            hoursEditor: { userId: null, from: '', to: '', hours: 0, saving: false },
            feedCopiedId: null,
            assignMode: false,
            autoBusy: null,          // checklist_id mid-Auto
            drawerChip: null,
            shiftDays: 1,
            adhocOpen: false,
            adhoc: { type: 'adhoc', title: '', userId: null, date: '', minutes: 60 },
            feedRate: null,
            feedSaving: false,
            deadlineDate: null,
            deadlineWas: null,
            deadlineSaving: false,
            jobSearch: '',
            searchOpen: false,
            pullBusy: false,
            feedDistributing: false,
            panelRefresh: 0,         // bumped per board fetch — resyncs the job progress panel
            toast: null,             // { msg, undo } — post-drag receipt
            // multi-select → bulk move. `selected` is keyed by slot id and
            // holds a snapshot of each chip, so a selection outlives paging
            // and refetches (pick this week, page on, pick more, move all).
            selecting: false,
            selected: {},
            bulkOpen: false,
            bulkBusy: false,         // the real move request is in flight — the page owns it, not the panel
            bulkError: null,
            lastPickedQuote: null,   // powers the "+ all of Qnnn in view" shortcut
        };
    },

    watch: {
        quoteFilter() {
            this.syncFeedRate();
            this.syncDeadline();
            this.feedDistributing = false;
            // Keep the search box telling the truth however the filter was
            // set (search pick, lane-bar click, deep link, clear pill).
            const opt = this.quoteFilter
                ? this.quoteOptions.find(q => q.id === this.quoteFilter) : null;
            this.jobSearch = opt ? `Q${opt.id} · ${opt.name}` : '';
            this.searchOpen = false;

            // If the job's work lives outside the visible window the filter
            // would show an empty grid — jump to the week its remaining
            // work starts instead of making people page through weeks.
            // NOT on the wall boards: there the filter only opens the job
            // panel, the board still shows everyone's month, and yanking
            // the window back to an old job's first slot loses Frances's
            // place for no gain.
            if (this.quoteFilter && this.board.dates.length && !this.isBoardZoom) {
                const visible = (this.board.slots || []).some(s => s.quote_id === this.quoteFilter);
                const job = (this.board.jobs || []).find(j => j.quote_id === this.quoteFilter);
                const target = job && (job.next_date || job.first_date);
                if (!visible && target) {
                    this.anchor = target;
                    this.fetchBoard();
                }
            }
        },
    },

    created() {
        const params = new URLSearchParams(window.location.search);
        if (params.get('quote')) {
            this.quoteFilter = parseInt(params.get('quote'), 10) || null;
            this.assignMode = params.get('assign') === '1' && !!this.quoteFilter;
        }
        // /planner/checklist and ?view= both land on the named view, so the
        // nav link, bookmarks and refreshes all keep their place (the zoom
        // used to reset to Week on every visit — findability defect #1).
        const view = params.get('view')
            || (window.location.pathname.replace(/\/+$/, '').endsWith('/checklist') ? 'checklist' : null);
        if (['day', 'week', 'month', 'dates', 'people', 'checklist'].indexOf(view) !== -1) {
            this.zoom = view;
        }
        if (['week', 'month', '28d'].indexOf(params.get('range')) !== -1) {
            this.rangeMode = params.get('range');
        }
        this.fetchBoard();

        // Esc backs out of the bulk panel, then out of select mode.
        this._onKeydown = e => this.onKeydown(e);
        document.addEventListener('keydown', this._onKeydown);

        // Live refresh: another planner's writes refetch this board.
        if (window.Echo) {
            try {
                window.Echo.channel('planner').listen('.slot-changed', () => {
                    if (this._echoTimer) clearTimeout(this._echoTimer);
                    this._echoTimer = setTimeout(() => {
                        if (this.dragging || this.childDragging) { this._boardStale = true; return; }
                        this.fetchBoard();
                    }, 800);
                });
            } catch (e) {
                // Echo not configured on this environment — polling not needed for v1
            }
        }
    },

    beforeDestroy() {
        if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
        if (this._toastTimer) clearTimeout(this._toastTimer);
    },

    computed: {
        canWrite() {
            return this.permissions.can_place || this.permissions.can_replan;
        },

        dragOpts() {
            return DRAG_OPTS;
        },

        dragTargetRow() {
            return this.hoverCell ? parseInt(this.hoverCell.split('|')[0], 10) : null;
        },

        savedFeedRate() {
            return (this.quoteFilter && this.board.feed_rates)
                ? (this.board.feed_rates[this.quoteFilter] || null) : null;
        },
        deadlineChanged() {
            return this.deadlineDate !== this.deadlineWas;
        },

        isBoardZoom() {
            return this.zoom === 'people' || this.zoom === 'dates';
        },
        /* The wall boards' window from the anchor + range mode — Monday-
           anchored weeks, the same arithmetic as QAPP\PlannerWeeks so a date
           lands in the same row server- and client-side. */
        boardRange() {
            const anchor = new Date(this.anchor);
            if (this.rangeMode === 'month') {
                const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
                const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
                const start = this.mondayOf(first);
                const lastMon = this.mondayOf(last);
                const weeks = Math.round((new Date(lastMon) - new Date(start)) / (7 * 86400000)) + 1;
                return { start, weeks, label: first.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) };
            }
            const start = this.mondayOf(anchor);
            const weeks = this.rangeMode === 'week' ? 1 : 4;
            const end = new Date(start);
            end.setDate(end.getDate() + weeks * 7 - 1);
            const label = weeks === 1
                ? 'w/c ' + new Date(start).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
                : new Date(start).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) + ' – '
                    + end.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
            return { start, weeks, label };
        },

        rangeLabel() {
            if (this.isBoardZoom) return this.boardRange.label;
            if (!this.board.dates.length) return '';
            const a = new Date(this.board.dates[0]);
            const b = new Date(this.board.dates[this.board.dates.length - 1]);
            if (this.zoom === 'day') {
                return a.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
            }
            if (this.zoom === 'month') {
                return a.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
            }
            const opts = { day: 'numeric', month: 'long' };
            return a.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' }) + ' – '
                + b.toLocaleDateString('en-GB', { weekday: 'short', ...opts }) + ' ' + b.getFullYear();
        },

        assignGroup() {
            return this.board.tray.find(g => g.quote_id === this.quoteFilter) || null;
        },
        assignTotals() {
            if (!this.assignGroup) return { total: 0, placed: 0 };
            const total = this.assignGroup.total_open || this.assignGroup.processes.length;
            return { total, placed: Math.max(0, total - this.assignGroup.processes.length) };
        },
        assignQuoteName() {
            if (this.assignGroup) return this.assignGroup.quote_name;
            const opt = this.quoteOptions.find(q => q.id === this.quoteFilter);
            return opt ? opt.name : '';
        },

        gridColumns() {
            return { gridTemplateColumns: '172px repeat(' + this.board.dates.length + ', minmax(150px, 1fr)) 106px' };
        },

        quoteOptions() {
            const seen = {};
            this.board.slots.forEach(s => { if (s.quote_id) seen[s.quote_id] = s.quote_name; });
            this.board.tray.forEach(g => { seen[g.quote_id] = g.quote_name; });
            // Jobs lane too — covers card-only ghosts (trickle orders etc.)
            (this.board.jobs || []).forEach(j => { seen[j.quote_id] = j.quote_name; });
            // Removed jobs stay findable so they can be opened and restored.
            (this.board.removed_jobs || []).forEach(j => {
                if (!seen[j.quote_id]) seen[j.quote_id] = j.quote_name + ' (removed)';
            });
            return Object.keys(seen).map(id => ({ id: parseInt(id, 10), name: seen[id] }))
                .sort((a, b) => b.id - a.id);
        },

        jobMatches() {
            const needle = this.jobSearch.trim().toLowerCase();
            if (!needle) return [];
            return this.quoteOptions
                .filter(q => String(q.id).includes(needle.replace(/^q/, ''))
                    || (q.name || '').toLowerCase().includes(needle))
                .slice(0, 8);
        },

        visibleTray() {
            return this.board.tray.filter(g => !this.quoteFilter || g.quote_id === this.quoteFilter);
        },

        /* lane bars positioned/clipped against the visible window */
        visibleJobs() {
            const n = this.board.dates.length;
            if (!n) return [];
            const first = this.board.dates[0];
            const last = this.board.dates[n - 1];

            return this.board.jobs
                .filter(j => !this.quoteFilter || j.quote_id === this.quoteFilter)
                .filter(j => this.matchesFlagFilter(j))
                .map(j => {
                    const li = this.dateIndex(j.first_date, first);
                    const ri = this.dateIndex(j.last_date, first) + 1;
                    const L = Math.max(li, 0);
                    const R = Math.min(ri, n);
                    if (R <= 0 || L >= n) return null;

                    let flagLeft = null;
                    if (j.deadline) {
                        const fi = this.dateIndex(j.deadline, first) + 0.5;
                        if (fi >= L && fi <= R) flagLeft = ((fi - L) / (R - L)) * 100;
                    }

                    return Object.assign({}, j, {
                        left: (L / n) * 100,
                        width: ((R - L) / n) * 100,
                        clipL: li < 0,
                        clipR: ri > n,
                        flagLeft,
                    });
                })
                .filter(Boolean);
        },

        totalJobsValue() {
            return this.visibleJobs.reduce((sum, j) => sum + (j.value_pence || 0), 0);
        },

        // ---------- multi-select ----------
        selectedList() {
            return Object.values(this.selected).sort((a, b) =>
                a.date < b.date ? -1 : a.date > b.date ? 1 : (a.user_id - b.user_id) || (a.pos - b.pos));
        },
        selectedCount() {
            return this.selectedList.length;
        },
        selectedSeconds() {
            return this.selectedList.reduce((sum, s) => sum + (s.planned_seconds || 0), 0);
        },
        selectedJobs() {
            const ids = {};
            this.selectedList.forEach(s => { ids[s.quote_id || ('t' + s.id)] = true; });
            return Object.keys(ids).length;
        },
        selectedOffView() {
            const onBoard = {};
            (this.board.slots || []).forEach(s => { onBoard[s.id] = true; });
            return this.selectedList.filter(s => !onBoard[s.id]).length;
        },
        jobChipsToAdd() {
            return this.lastPickedQuote ? this.jobChipsInView(this.lastPickedQuote).length : 0;
        },
    },

    methods: {
        // ---------- data ----------
        fetchParams() {
            if (this.zoom === 'day') {
                return { start: this.anchor, days: 1 };
            }
            if (this.zoom === 'month') {
                const d = new Date(this.anchor);
                const first = new Date(d.getFullYear(), d.getMonth(), 1);
                const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
                return { start: this.ymd(first), days: daysInMonth };
            }
            return { start: this.mondayOf(new Date(this.anchor)), days: 5 };
        },

        fetchBoard() {
            this.loading = true;
            const seq = this._fetchSeq = (this._fetchSeq || 0) + 1;
            return window.axios.get('/planner/board', { params: this.fetchParams() })
                .then(res => {
                    if (seq !== this._fetchSeq) return;  // superseded by a newer fetch
                    // Never rebuild the lists mid-drag — Sortable is holding
                    // DOM nodes inside them, and swapping the arrays under it
                    // kills the drag. Refetch once the chip is put down.
                    if (this.dragging || this.childDragging) {
                        this._boardStale = true;
                        return;
                    }
                    this.applyBoard(res.data);
                })
                .catch(err => { this.error = this.errMsg(err); })
                .then(() => { this.loading = false; });
        },

        applyBoard(data) {
            this.board = data;
            this.permissions = data.permissions || this.permissions;
            // Shop-floor tier: the checklist is their whole planner — the
            // board sends them no people/tray, so the other zooms would
            // render an empty shell.
            if (!this.permissions.full_board && this.zoom !== 'checklist') {
                this.zoom = 'checklist';
                this.syncViewUrl();
            }
            if (!this.adhoc.date) this.adhoc.date = data.today;
            this.rebuildCells();
            this.syncFeedRate();
            this.syncDeadline();
            this.panelRefresh++;
            // An open drawer holds a reference into the OLD board — re-point
            // it so saves made from the drawer (est minutes, people) show up
            // without closing and reopening it.
            if (this.drawerChip && this.drawerChip.id) {
                const fresh = data.slots.find(s => s.id === this.drawerChip.id);
                if (fresh) this.drawerChip = fresh;
            }
            // Selection snapshots: refresh from the fresh board and drop
            // chips that got ticked meanwhile. Ids outside the fetched
            // window are kept on purpose — that's how a cross-week pick works.
            Object.keys(this.selected).forEach(id => {
                const fresh = data.slots.find(s => String(s.id) === String(id));
                if (!fresh) return;
                if (fresh.checked) { this.$delete(this.selected, id); return; }
                this.$set(this.selected, id, this.snapshot(fresh));
            });
        },

        refetchIfStale() {
            if (this._boardStale && !this.dragging && !this.childDragging) {
                this._boardStale = false;
                this.fetchBoard();
            }
        },

        // ---------- zoom + assign mode ----------
        setZoom(zoom) {
            if (this.zoom === zoom) return;
            if (zoom !== 'week' && zoom !== 'day') this.exitSelect();
            this.zoom = zoom;
            this.syncViewUrl();
            this.fetchBoard();
        },
        /* Keep the view in the URL so refresh/back/bookmark return to the
           same place. The /planner/checklist deep link normalises to
           /planner?view=… once the user switches view from inside it. */
        syncViewUrl() {
            try {
                const url = new URL(window.location.href);
                url.pathname = url.pathname.replace(/\/checklist\/?$/, '');
                url.searchParams.set('view', this.zoom);
                if (this.isBoardZoom) {
                    url.searchParams.set('range', this.rangeMode);
                } else {
                    url.searchParams.delete('range');
                }
                history.replaceState(null, '', url.toString());
            } catch (e) {
                // cosmetic only — old browsers keep working without it
            }
        },
        gotoDay(date) {
            this.anchor = date;
            this.zoom = 'day';
            this.fetchBoard();
        },

        // ---------- the wall boards (Dates / People) ----------
        setRangeMode(mode) {
            if (this.rangeMode === mode) return;
            this.rangeMode = mode;
            this.syncViewUrl();
        },
        toggleFlagFilter(flag) {
            this.flagFilter = this.flagFilter === flag ? null : flag;
        },
        /* One rule for every surface that filters by flag (lane, checklist,
           boards): the job's flags decide; absence never matches. */
        matchesFlagFilter(job) {
            if (!this.flagFilter) return true;
            const f = (job && job.flags) || {};
            if (this.flagFilter === 'artwork_waiting') return f.artwork === 'waiting';
            if (this.flagFilter === 'priority') return !!f.priority;
            return true;
        },
        jobFlags(quoteId) {
            if (!quoteId) return null;
            const job = (this.board.jobs || []).find(j => j.quote_id === quoteId);
            return job ? (job.flags || null) : null;
        },
        /* Chip corner mark — the two flags a person at the bench acts on. */
        chipMark(slot) {
            const f = this.jobFlags(slot.quote_id);
            if (!f) return '';
            return (f.artwork === 'waiting' ? '⏳' : '') + (f.priority ? '★' : '');
        },
        chipMarkTitle(slot) {
            const f = this.jobFlags(slot.quote_id) || {};
            const parts = [];
            if (f.artwork === 'waiting') parts.push('Waiting on artwork' + (f.artwork_by ? ' (' + f.artwork_by + ')' : ''));
            if (f.priority) parts.push('Priority job');
            return parts.join(' · ');
        },
        openJob(quoteId) {
            if (!quoteId) return;
            this.quoteFilter = quoteId;
        },
        /* A card dropped on another (person, week) cell on the People board:
           { slot_ids, slots, target, label }. Dry-run first — if every slot
           moves, commit at once and hand the receipt + Undo to onBulkMoved;
           if anything is refused, open the bulk panel pre-loaded with that
           selection and target so each refusal is named. No new server
           rules, one receipt path. */
        onBoardMove(req) {
            if (this.boardBusy || this.bulkBusy || !req || !req.slot_ids || !req.slot_ids.length) return;
            this.boardBusy = true;
            const payload = { slot_ids: req.slot_ids, target: req.target };
            window.axios.post('/planner/slots/move', Object.assign({ dry_run: true }, payload))
                .then(res => {
                    const refused = res.data.refused || [];
                    const moved = res.data.moved || [];
                    if (!refused.length && moved.length) {
                        return window.axios.post('/planner/slots/move', payload)
                            .then(r => this.onBulkMoved(r.data, req.label));
                    }
                    if (!refused.length && !moved.length) {
                        this.error = 'Nothing to move — that work is already done.';
                        this.fetchBoard();
                        return;
                    }
                    (req.slots || []).forEach(s => this.$set(this.selected, s.id, this.snapshot(s)));
                    this.bulkPreset = req.target;
                    this.bulkOpen = true;
                    this.fetchBoard();   // the card snapped back client-side; make the board honest
                })
                .catch(err => { this.error = this.errMsg(err); this.fetchBoard(); })
                .then(() => { this.boardBusy = false; });
        },
        /* A ⚑ card dropped on another day on the Dates board — the job's
           deadline moves through the same endpoint as the toolbar picker,
           with a receipt that puts the old date back. */
        onSetDeadline(ev) {
            if (!ev || !ev.quote_id || !ev.date) return;
            window.axios.post('/planner/quote/' + ev.quote_id + '/deadline', { deadline: ev.date })
                .then(() => {
                    this.error = null;
                    const who = ev.quote_name ? ev.quote_name : 'Q' + ev.quote_id;
                    this.showToast('Deadline for ' + who + ' → ' + this.fmtDayShort(ev.date),
                        ev.from ? () => this.onSetDeadline({ quote_id: ev.quote_id, date: ev.from, quote_name: ev.quote_name }) : null);
                    this.fetchBoard();
                })
                .catch(err => { this.error = this.errMsg(err); this.fetchBoard(); });
        },
        fmtDayShort(date) {
            return new Date(date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
        },

        enterAssign(quoteId) {
            this.quoteFilter = quoteId;
            this.assignMode = true;
            if (this.zoom !== 'week') {
                this.zoom = 'week';
                this.fetchBoard();
            }
        },
        exitAssign() {
            this.assignMode = false;
            this.quoteFilter = null;
        },
        sheetUrl(itemId) {
            return productionSheetUrl(itemId);
        },
        pickJob(m) {
            this.quoteFilter = m.id;
            this.searchOpen = false;
        },
        pickFirstMatch() {
            if (this.jobMatches.length) this.pickJob(this.jobMatches[0]);
        },
        closeSearchSoon() {
            setTimeout(() => { this.searchOpen = false; }, 150);
        },
        pullToday() {
            if (!this.quoteFilter) return;
            if (!window.confirm('Pull ALL of this job\'s unfinished work onto today?\n\nPeople are kept; same-person slots from different days merge. Assign it out from the Day view after.')) return;
            this.pullBusy = true;
            window.axios.post('/planner/quote/' + this.quoteFilter + '/pull-today')
                .then(() => {
                    this.error = null;
                    // Land where the work now is: today, Day zoom.
                    this.gotoDay(this.board.today);
                })
                .catch(err => { this.error = this.errMsg(err); })
                .then(() => { this.pullBusy = false; });
        },
        // Lane bar click: focus the board on that job; same bar again
        // (or the ✕ clear pill) shows everything. Never yanks the user
        // out of assign mode — that has its own Done button.
        toggleLaneFilter(quoteId) {
            if (this.assignMode) return;
            this.quoteFilter = this.quoteFilter === quoteId ? null : quoteId;
        },
        autoPlace(proc) {
            this.autoBusy = proc.checklist_id;
            window.axios.post('/planner/slot/auto', { production_checklist_id: proc.checklist_id })
                .then(() => this.fetchBoard())
                .catch(err => { this.error = this.errMsg(err); })
                .then(() => { this.autoBusy = null; });
        },
        doShift() {
            if (!this.shiftDays || !this.quoteFilter) return;
            window.axios.post('/planner/quote/' + this.quoteFilter + '/shift', { days: this.shiftDays })
                .then(res => {
                    this.error = null;
                    this.fetchBoard();
                })
                .catch(err => { this.error = this.errMsg(err); });
        },

        // ---------- mega-order trickle feed ----------
        syncFeedRate() {
            this.feedRate = (this.quoteFilter && this.board.feed_rates)
                ? (this.board.feed_rates[this.quoteFilter] || null) : null;
        },
        syncDeadline() {
            const job = this.quoteFilter
                ? (this.board.jobs || []).find(j => j.quote_id === this.quoteFilter) : null;
            this.deadlineDate = job ? (job.deadline || null) : null;
            this.deadlineWas = this.deadlineDate;
        },
        saveDeadline() {
            if (!this.quoteFilter || !this.deadlineDate) return;
            this.deadlineSaving = true;
            window.axios.post('/planner/quote/' + this.quoteFilter + '/deadline', { deadline: this.deadlineDate })
                .then(() => { this.error = null; this.fetchBoard(); })
                .catch(err => { this.error = this.errMsg(err); })
                .then(() => { this.deadlineSaving = false; });
        },
        saveFeedRate() {
            if (!this.quoteFilter) return;
            this.feedSaving = true;
            const rate = this.feedRate ? parseInt(this.feedRate, 10) : null;
            window.axios.post('/planner/quote/' + this.quoteFilter + '/feed-rate', { units_per_week: rate })
                .then(res => {
                    const fr = Object.assign({}, this.board.feed_rates);
                    if (res.data.units_per_week) {
                        fr[this.quoteFilter] = res.data.units_per_week;
                    } else {
                        delete fr[this.quoteFilter];
                    }
                    this.board.feed_rates = fr;
                    this.error = null;
                })
                .catch(err => { this.error = this.errMsg(err); })
                .then(() => { this.feedSaving = false; });
        },
        distributeFeed() {
            if (!this.quoteFilter || !this.savedFeedRate) return;
            this.feedDistributing = true;
            window.axios.post('/planner/quote/' + this.quoteFilter + '/distribute', {})
                .then(() => {
                    // Placement runs detached (~1–2 min); pull the board twice
                    // so it appears without a manual refresh.
                    setTimeout(() => this.fetchBoard(), 45000);
                    setTimeout(() => { this.fetchBoard(); this.feedDistributing = false; }, 95000);
                })
                .catch(err => { this.error = this.errMsg(err); this.feedDistributing = false; });
        },
        clearFeedRate() {
            this.feedRate = null;
            this.saveFeedRate();
        },
        addTyped() {
            window.axios.post('/planner/slot/typed', {
                type: this.adhoc.type,
                title: this.adhoc.title,
                user_id: this.adhoc.userId,
                scheduled_date: this.adhoc.date,
                minutes: this.adhoc.minutes,
            })
                .then(() => {
                    this.adhocOpen = false;
                    this.adhoc = { type: 'adhoc', title: '', userId: null, date: '', minutes: 60 };
                    this.fetchBoard();
                })
                .catch(err => { this.error = this.errMsg(err); });
        },
        openDrawer(slot) {
            if (this.dragging) return;
            this.drawerChip = slot;
        },

        // ---------- multi-select → bulk move ----------
        /* One click handler for every chip on Week and Day: Ctrl/Cmd-click
           flips into select mode with that chip picked; in select mode a
           click toggles; otherwise it's the drawer as before. */
        chipClick(slot, evt) {
            if (this.dragging) return;
            const modifier = evt && (evt.ctrlKey || evt.metaKey);
            if (modifier && !this.selecting && this.canWrite) {
                this.enterSelect();
            }
            if (this.selecting) {
                if (this.slotMovable(slot)) this.toggleSelect(slot);
                return;
            }
            this.openDrawer(slot);
        },
        enterSelect() {
            this.selecting = true;
            this.drawerChip = null;
        },
        exitSelect() {
            if (this.bulkBusy) return;
            this.selecting = false;
            this.bulkOpen = false;
            this.selected = {};
            this.lastPickedQuote = null;
        },
        clearSelection() {
            this.selected = {};
            this.lastPickedQuote = null;
        },
        toggleSelect(slot) {
            if (this.selected[slot.id]) {
                this.$delete(this.selected, slot.id);
            } else {
                this.$set(this.selected, slot.id, this.snapshot(slot));
            }
            this.lastPickedQuote = slot.quote_id || null;
        },
        unselect(id) {
            if (this.bulkBusy) return;
            this.$delete(this.selected, id);
            if (!this.selectedCount) this.bulkOpen = false;
        },
        /* Every movable chip of the job that's on the board right now —
           jobs are the unit Frances thinks in, and this covers "push the
           whole job a week" with an exact undo. */
        selectJobInView(quoteId) {
            this.jobChipsInView(quoteId).forEach(s => this.$set(this.selected, s.id, this.snapshot(s)));
        },
        jobChipsInView(quoteId) {
            return (this.board.slots || []).filter(s => s.quote_id === quoteId && this.slotMovable(s) && !this.selected[s.id]);
        },
        snapshot(s) {
            return {
                id: s.id, pretty_name: s.pretty_name, type: s.type,
                quote_id: s.quote_id, quote_name: s.quote_name,
                user_id: s.user_id, date: s.date, pos: s.pos,
                planned_seconds: s.planned_seconds, workcentre_id: s.workcentre_id,
                released: s.released, checked: s.checked, item_id: s.item_id,
            };
        },
        onKeydown(e) {
            if (e.key !== 'Escape' || !this.selecting) return;
            if (this.bulkOpen) { this.closeBulk(); return; }
            if (this.bulkBusy) return;
            this.exitSelect();
        },
        closeBulk() {
            if (this.bulkBusy) return;   // never lose a receipt to a stray click
            this.bulkOpen = false;
            this.bulkError = null;
            this.bulkPreset = null;
            // A board-originated selection has no selection bar to clear it from.
            if (this.isBoardZoom) this.selected = {};
        },
        /* The page owns the real move so the receipt survives the panel:
           a request sent from a component that is then destroyed lands on
           nothing — the server has moved the chips, nobody hears about it. */
        doBulkMove(payload, label) {
            if (this.bulkBusy) return;
            this.bulkBusy = true;
            this.bulkError = null;
            window.axios.post('/planner/slots/move', payload)
                .then(res => this.onBulkMoved(res.data, label))
                .catch(err => { this.bulkError = this.errMsg(err); })
                .then(() => { this.bulkBusy = false; });
        },
        /* Land the receipt, keep any refused chips selected for a retry,
           and follow the work if it left the view. */
        onBulkMoved(result, label) {
            this.bulkOpen = false;
            this.bulkError = null;
            this.bulkPreset = null;
            const moved = result.moved || [];
            const refused = (result.refused || []).length;
            moved.forEach(m => this.$delete(this.selected, m.slot_id));

            let msg = 'Moved ' + moved.length + ' chip' + (moved.length === 1 ? '' : 's') + (label ? ' → ' + label : '');
            if (refused) msg += ' · ' + refused + ' not moved';
            const past = moved.filter(m => m.backdated);
            this.showToast(msg,
                moved.length ? () => this.undoBulk(moved) : null,
                past.length ? () => this.markDone(past.map(m => ({ id: m.slot_id, checklist_id: m.checklist_id }))) : null,
                past.length ? 'Mark ' + past.length + ' done' : null);

            this.followDates(moved.map(m => m.to.date));
            this.fetchBoard();
        },
        undoBulk(moved) {
            const moves = moved.map(m => ({
                slot_id: m.slot_id, user_id: m.from.user_id, scheduled_date: m.from.date, pos: m.from.pos,
            }));
            window.axios.post('/planner/slots/move', { moves: moves })
                .then(res => {
                    const back = (res.data.moved || []).length;
                    const stuck = (res.data.refused || []).length;
                    this.showToast('Put ' + back + ' chip' + (back === 1 ? '' : 's') + ' back'
                        + (stuck ? ' · ' + stuck + ' couldn\'t go back' : ''));
                    this.followDates(moved.map(m => m.from.date));
                    this.fetchBoard();
                })
                .catch(err => { this.error = this.errMsg(err); });
        },
        /* Judge "in view" from the anchor's window, not board.dates — a
           quick Move → Undo can run before the first refetch has landed,
           and the stale board would say the chips are still on screen. */
        followDates(dates) {
            const sorted = dates.filter(Boolean).sort();
            if (!sorted.length) return;
            const p = this.fetchParams();
            const end = new Date(p.start);
            end.setDate(end.getDate() + (p.days - 1));
            const last = this.ymd(end);
            const inView = sorted.some(d => d >= p.start && d <= last);
            if (!inView) this.anchor = sorted[0];
        },

        rebuildCells() {
            const cells = {};
            this.board.people.forEach(p => {
                this.board.dates.forEach(d => { cells[p.id + '|' + d] = []; });
            });
            this.board.slots.forEach(s => {
                const key = s.user_id + '|' + s.date;
                if (!cells[key]) cells[key] = [];
                cells[key].push(s);
            });
            Object.keys(cells).forEach(k => cells[k].sort((a, b) => a.pos - b.pos));
            this.cells = cells;
        },

        cellList(userId, date) {
            const key = userId + '|' + date;
            if (!this.cells[key]) this.$set(this.cells, key, []);
            return this.cells[key];
        },

        // ---------- drag & drop ----------
        onDragStart(evt, list) {
            this.dragging = list[evt.oldIndex] || null;
        },
        onDragEnd() {
            this.dragging = null;
            this.hoverCell = null;
            this.refetchIfStale();
        },
        onChildDrag(active) {
            this.childDragging = active;
            if (!active) this.refetchIfStale();
        },

        /* SortableJS move hook: false blocks the drop. Mirrors the server's
           SlotWriteGuard/SlotSequencer rules; the server stays authoritative.
           Also tracks the would-be drop cell so the grid can spotlight the
           receiving person + day before the chip is released. */
        onMoveCheck(evt) {
            const el = evt.draggedContext.element;
            const to = evt.to || {};
            const date = to.dataset ? to.dataset.date : null;
            if (!date) { this.hoverCell = null; return false; }   // tray is put:false anyway
            if (!this.elementMovable(el)) { this.hoverCell = null; return false; }
            const ok = this.invalidReason(el, date) === null;
            this.hoverCell = ok ? (to.dataset.userId + '|' + date) : null;
            return ok;
        },

        elementMovable(el) {
            if (!el) return false;
            if (el.checklist_id && el.pretty_name && el.id === undefined) {
                // tray process — placing needs pre-production rights (or replanner for late adds)
                return el.late_add ? this.permissions.can_replan : this.permissions.can_place;
            }
            return this.slotMovable(el);
        },

        slotMovable(slot) {
            if (slot.checked) return false;
            return slot.released ? this.permissions.can_replan : this.permissions.can_place;
        },

        /* null = legal target; string = why not (matches server copy).
           Past days are legal for planners since 2026-09-08 — the server
           audits the write as backdated and only a far-back floor refuses. */
        invalidReason(el, date) {
            if (!el) return null;

            if (!el.item_id || !el.workcentre_id) return null;
            const myOrder = this.wcOrder(el.workcentre_id);
            if (myOrder === null) return null;

            const mins = {};
            this.board.slots.forEach(s => {
                if (s.item_id !== el.item_id || s.checklist_id === el.checklist_id || s.checked) return;
                if (!s.workcentre_id) return;
                const order = this.wcOrder(s.workcentre_id);
                if (order === null || order >= myOrder || this.wcConcurrent(s.workcentre_id)) return;
                if (!mins[s.checklist_id] || s.date < mins[s.checklist_id].date) {
                    mins[s.checklist_id] = s;
                }
            });

            const blocker = Object.values(mins).find(s => s.date > date);
            return blocker ? ('before ' + blocker.pretty_name) : null;
        },

        dropReason(person, date) {
            return this.dragging ? this.invalidReason(this.dragging, date) : null;
        },

        cellClasses(person, date) {
            return {
                'pl-cell-today': date === this.board.today,
                'pl-cell-past': date < this.board.today,
                'pl-cell-weekend': this.isWeekend(date),
                'pl-cell-invalid': !!(this.dragging && this.dropReason(person, date)),
                'pl-cell-target': this.hoverCell === person.id + '|' + date,
                'pl-row-dim': this.rowDimmed(person),
            };
        },

        rowDimmed(person) {
            if (!this.dragging || !this.dragging.workcentre_id) return false;
            const ids = person.workcentre_ids || [];
            return ids.length > 0 && ids.indexOf(this.dragging.workcentre_id) === -1;
        },

        onCellChange(userId, date, evt) {
            if (evt.added) {
                const el = evt.added.element;
                const list = this.cellList(userId, date);
                const pos = this.midpointPos(list, evt.added.newIndex);
                if (el.id !== undefined) {
                    this.moveSlot(el, userId, date, pos);
                } else {
                    this.placeTrayProcess(el, userId, date, pos);
                }
            } else if (evt.moved) {
                const list = this.cellList(userId, date);
                const el = evt.moved.element;
                const pos = this.midpointPos(list, evt.moved.newIndex);
                this.moveSlot(el, userId, date, pos);
            }
            // evt.removed: handled by the paired `added` on the target cell
        },

        midpointPos(list, index) {
            const prev = list[index - 1];
            const next = list[index + 1];
            if (prev && next) return (prev.pos + next.pos) / 2;
            if (prev) return prev.pos + 1;
            if (next) return next.pos / 2;
            return 1;
        },

        moveSlot(slot, userId, date, pos) {
            // slot still carries its pre-move server fields — that's the undo target
            const from = { user_id: slot.user_id, date: slot.date, pos: slot.pos };
            const changed = from.user_id !== userId || from.date !== date;
            window.axios.put('/planner/slot/' + slot.id, {
                user_id: userId,
                scheduled_date: date,
                pos: pos,
            })
                .then(() => {
                    if (changed) {
                        const backdated = date < this.board.today && !slot.checked;
                        this.showToast('Moved ' + slot.pretty_name + ' → ' + this.personName(userId)
                            + (from.date !== date ? ' · ' + this.toastDay(date) : ''),
                            () => this.undoMove(slot.id, from, slot.pretty_name),
                            backdated ? () => this.markDone([slot]) : null);
                    }
                    this.fetchBoard();
                })
                .catch(err => { this.error = this.errMsg(err); this.fetchBoard(); });
        },

        placeTrayProcess(proc, userId, date, pos) {
            window.axios.post('/planner/slot', {
                production_checklist_id: proc.checklist_id,
                user_id: userId,
                scheduled_date: date,
                is_draft: false,
            })
                .then(res => {
                    const slot = res.data && res.data.slot;
                    if (slot && slot.id) {
                        this.showToast('Placed ' + proc.pretty_name + ' with ' + this.personName(userId)
                            + ' · ' + this.toastDay(date),
                            () => this.undoPlace(slot.id, proc.pretty_name));
                    }
                    this.fetchBoard();
                })
                .catch(err => { this.error = this.errMsg(err); this.fetchBoard(); });
        },

        // ---------- drop receipt toast ----------
        /* `done` is offered when work landed on a past day: most back-dating
           is recording something that already happened, and ticking it from
           the receipt keeps it out of the carryover rail. */
        /* Receipt for "Remove from planner" — Undo puts every slot back
           exactly where it was (the panel's Restore button is the later,
           today-onwards path). */
        onJobRemoved(ev) {
            const r = ev.result || {};
            let msg = 'Q' + ev.quoteId + ' removed from the planner — '
                + r.removed_slots + ' slot' + (r.removed_slots === 1 ? '' : 's') + ' cleared';
            if (r.in_progress && r.in_progress.length) msg += ' · tell ' + r.in_progress.join(', ');
            this.showToast(msg, () => this.restoreJob(ev.quoteId, 'exact'));
        },
        onJobRestored(ev) {
            const r = ev.result || {};
            this.showToast('Q' + ev.quoteId + ' is back on the planner — '
                + r.reinstated + ' slot' + (r.reinstated === 1 ? '' : 's') + ' reinstated'
                + (r.to_tray ? ', ' + r.to_tray + ' in the tray' : ''));
        },
        restoreJob(quoteId, mode) {
            window.axios.post('/planner/quote/' + quoteId + '/restore', { mode: mode })
                .then(res => {
                    this.onJobRestored({ quoteId: quoteId, result: res.data });
                    this.fetchBoard();
                })
                .catch(err => { this.error = this.errMsg(err); });
        },
        showToast(msg, undo, done, doneLabel) {
            if (this._toastTimer) clearTimeout(this._toastTimer);
            this.toast = { msg: msg, undo: undo || null, done: done || null, doneLabel: doneLabel || null };
            this._toastTimer = setTimeout(() => { this.toast = null; }, done ? 15000 : 10000);
        },
        dismissToast() {
            if (this._toastTimer) clearTimeout(this._toastTimer);
            this.toast = null;
        },
        runUndo() {
            const t = this.toast;
            this.dismissToast();
            if (t && t.undo) t.undo();
        },
        runDone() {
            const t = this.toast;
            this.dismissToast();
            if (t && t.done) t.done();
        },
        /* Tick the rows behind a set of chips — one PUT per checklist row
           (a row ticks every slot of that process, as the drawer does),
           the typed-slot endpoint for ad-hoc / delivery entries. */
        markDone(chips) {
            const rows = {};
            const typed = [];
            (chips || []).forEach(c => {
                if (c.checklist_id) rows[c.checklist_id] = true;
                else if (c.id) typed.push(c.id);
            });
            const reqs = Object.keys(rows).map(id =>
                window.axios.put('/checklist', { type: 'process', id: parseInt(id, 10), checked: true }))
                .concat(typed.map(id => window.axios.post('/planner/slot/' + id + '/tick', { on: true })));
            if (!reqs.length) return;
            Promise.all(reqs)
                .then(() => {
                    const n = reqs.length;
                    this.showToast('Marked ' + n + (n === 1 ? ' process' : ' processes') + ' done');
                    this.fetchBoard();
                })
                .catch(err => { this.error = this.errMsg(err); this.fetchBoard(); });
        },
        undoMove(slotId, from, name) {
            window.axios.put('/planner/slot/' + slotId, {
                user_id: from.user_id,
                scheduled_date: from.date,
                pos: from.pos,
            })
                .then(() => { this.showToast('Put ' + name + ' back'); this.fetchBoard(); })
                .catch(err => { this.error = this.errMsg(err); });
        },
        undoPlace(slotId, name) {
            window.axios.delete('/planner/slot/' + slotId)
                .then(() => { this.showToast(name + ' is back in the tray'); this.fetchBoard(); })
                .catch(err => { this.error = this.errMsg(err); });
        },
        onDayMoved(m) {
            this.showToast('Moved ' + m.name + ' → ' + this.personName(m.to_user_id),
                () => this.undoMove(m.slot_id, m.from, m.name));
        },
        personName(userId) {
            const p = this.board.people.find(p => p.id === userId);
            return p ? p.name : 'someone';
        },
        toastDay(date) {
            return new Date(date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
        },

        // ---------- capacity ----------
        capacitySeconds(person, date) {
            return Math.round(((person.hours || {})[date] || 0) * 3600);
        },
        plannedSeconds(person, date) {
            return this.cellList(person.id, date).reduce((sum, s) => sum + (s.planned_seconds || 0), 0);
        },
        capPct(person, date) {
            return this.adjustedPct(this.plannedSeconds(person, date), this.capacitySeconds(person, date));
        },
        capBand(person, date) {
            return this.bandClass(this.capPct(person, date));
        },
        weekCapacity(person) {
            return this.board.dates.reduce((sum, d) => sum + this.capacitySeconds(person, d), 0);
        },
        weekPlanned(person) {
            return this.board.dates.reduce((sum, d) => sum + this.plannedSeconds(person, d), 0);
        },
        weekPct(person) {
            return this.adjustedPct(this.weekPlanned(person), this.weekCapacity(person));
        },
        /* the CMS day-% convention: efficiency-adjusted */
        adjustedPct(planned, capacity) {
            if (capacity <= 0) return 0;
            const raw = (planned / capacity) * 100;
            const eff = this.board.efficiency_percent || 100;
            return Math.round(raw * (100 + (100 - eff)) / 100);
        },
        bandClass(pct) {
            if (pct <= 50) return 'pl-b-low';
            if (pct <= 100) return 'pl-b-ok';
            if (pct <= 150) return 'pl-b-tight';
            return 'pl-b-over';
        },

        // ---------- auto-fill limit ----------
        openLimitEditor(person) {
            if (!this.permissions.can_edit_limits) return;
            this.hoursEditor.userId = null;
            this.limitEditor = {
                userId: person.id,
                percent: person.auto_fill_percent === null ? 100 : person.auto_fill_percent,
                saving: false,
            };
        },
        saveLimit(person) {
            this.limitEditor.saving = true;
            window.axios.put('/planner/user/' + person.id + '/auto-fill-limit', { percent: this.limitEditor.percent })
                .then(() => {
                    person.auto_fill_percent = this.limitEditor.percent;
                    this.limitEditor.userId = null;
                })
                .catch(err => { this.error = this.errMsg(err); })
                .then(() => { this.limitEditor.saving = false; });
        },

        copyPersonFeed(person) {
            copyText(person.feed_url)
                .then(() => {
                    this.feedCopiedId = person.id;
                    setTimeout(() => { this.feedCopiedId = null; }, 2500);
                })
                .catch(() => { this.error = 'Could not copy — open ' + person.name + '’s My Day for the link instead.'; });
        },

        // ---------- hours / holidays ----------
        openHoursEditor(person) {
            this.limitEditor.userId = null;
            this.hoursEditor = {
                userId: person.id,
                from: this.board.today,
                to: this.board.today,
                hours: 0,
                saving: false,
            };
        },
        saveHours(person, clear) {
            const payload = { from: this.hoursEditor.from, to: this.hoursEditor.to };
            if (!clear) payload.hours = this.hoursEditor.hours;
            this.hoursEditor.saving = true;
            window.axios.put('/planner/user/' + person.id + '/hours', payload)
                .then(() => {
                    this.hoursEditor.userId = null;
                    this.fetchBoard();
                })
                .catch(err => { this.error = this.errMsg(err); })
                .then(() => { this.hoursEditor.saving = false; });
        },

        // ---------- filters ----------
        chipVisible(slot) {
            if (this.quoteFilter && slot.quote_id !== this.quoteFilter) return false;
            if (this.wcFilter && slot.workcentre_id !== this.wcFilter) return false;
            return true;
        },

        // ---------- paging ----------
        page(dir) {
            const d = new Date(this.anchor);
            if (this.zoom === 'day') {
                d.setDate(d.getDate() + dir);
            } else if (this.zoom === 'month' || (this.isBoardZoom && this.rangeMode === 'month')) {
                d.setDate(1);
                d.setMonth(d.getMonth() + dir);
            } else if (this.isBoardZoom && this.rangeMode === '28d') {
                d.setDate(d.getDate() + dir * 28);
            } else {
                d.setDate(d.getDate() + dir * 7);
            }
            this.anchor = this.ymd(d);
            this.fetchBoard();
        },
        goToday() {
            this.anchor = this.ymd(new Date());
            this.fetchBoard();
        },
        mondayOf(d) {
            const c = new Date(d);
            const day = (c.getDay() + 6) % 7;   // Mon=0
            c.setDate(c.getDate() - day);
            return this.ymd(c);
        },
        ymd(d) {
            return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
        },

        // ---------- display helpers ----------
        /* Whether a tray/shelf group spans more than one item — only then
           is the item name worth a line on every tile (a single-item job's
           processes can't be confused with each other). */
        multiItemGroup(group) {
            const ids = {};
            (group.processes || []).forEach(p => { if (p.item_id) ids[p.item_id] = true; });
            return Object.keys(ids).length > 1;
        },
        procTitle(proc) {
            return proc.item_name ? proc.item_name + ' — ' + proc.pretty_name : proc.pretty_name;
        },
        /* The narrow tray column truncates hard, and Science UK's names
           lead with the size ("4ft circle ref 2K9AL, with bug hatch") so
           plain truncation would eat the ref — the one part that makes the
           unit assignable. When a "ref CODE" is present, show that; the
           full name stays in the tooltip and on the assign-shelf tiles. */
        trayItemLabel(proc) {
            if (!proc.item_name) return null;
            const m = proc.item_name.match(/\bref\.?\s*([A-Za-z0-9-]{3,10})\b/i);
            return m ? 'ref ' + m[1].toUpperCase() : proc.item_name;
        },
        wcName(wcId) {
            const wc = this.board.workcentres.find(w => w.id === wcId);
            return wc ? wc.name : null;
        },
        wcOrder(wcId) {
            const wc = this.board.workcentres.find(w => w.id === wcId);
            return wc ? wc.order : null;
        },
        wcConcurrent(wcId) {
            const wc = this.board.workcentres.find(w => w.id === wcId);
            return wc ? !!wc.concurrent : false;
        },
        wcColour(wcId, type) {
            if (type && type !== 'process') return NEUTRAL;
            return WC_COLOURS[this.wcName(wcId)] || NEUTRAL;
        },
        wcIcon(wcId, type) {
            if (type === 'delivery') return 'pl-van';
            if (type === 'adhoc') return 'pl-wrench';
            return WC_ICONS[this.wcName(wcId)] || 'pl-misc';
        },
        chipStyle(wcId, type) {
            return { '--plwc': this.wcColour(wcId, type) };
        },
        typeLabel(type) {
            return type === 'delivery' ? 'Delivery' : type === 'adhoc' ? 'Ad-hoc' : '';
        },
        initials(name) {
            return name.split(/\s+/).map(w => w[0]).join('').substr(0, 2).toUpperCase();
        },
        dayName(date) {
            return new Date(date).toLocaleDateString('en-GB', { weekday: 'short' });
        },
        dayNum(date) {
            return new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
        },
        isWeekend(date) {
            const n = new Date(date).getDay();
            return n === 0 || n === 6;
        },
        dateIndex(date, first) {
            return Math.round((new Date(date) - new Date(first)) / 86400000);
        },
        mins(m) {
            if (m >= 60) {
                const h = Math.floor(m / 60), r = m % 60;
                return r ? h + 'h ' + String(r).padStart(2, '0') : h + 'h';
            }
            return m + 'm';
        },
        secs(s) {
            return this.mins(Math.round(s / 60));
        },
        money(pence) {
            return '£' + Math.round(pence / 100).toLocaleString('en-GB');
        },
        errMsg(err) {
            if (err.response && err.response.data) {
                return err.response.data.error || err.response.data.message || ('Request failed (' + err.response.status + ')');
            }
            return 'Request failed — check your connection';
        },
    },
};
</script>

<style scoped>
/* ---- wall boards: range control, flag filters, chip corner mark ---- */
.pl-range-ctl{display:inline-flex; background:var(--pl-card2); border-radius:8px; padding:2px; gap:2px}
.pl-range-ctl button{border:0; background:transparent; border-radius:6px; padding:6px 12px; font-weight:700; font-size:13px; color:var(--pl-ink2); white-space:nowrap}
.pl-range-ctl button.on{background:var(--pl-ink); color:#fff}
.pl-flagbtn{border:1px solid var(--pl-line); background:var(--pl-card); border-radius:20px; padding:6px 11px; font-weight:700; font-size:12.5px; color:var(--pl-ink2); white-space:nowrap}
.pl-flagbtn.on{background:var(--pl-ink); border-color:var(--pl-ink); color:#fff}
.pl-chip-flag{position:absolute; top:-7px; right:-6px; background:var(--pl-card); border:1px solid var(--pl-line); border-radius:10px;
    font-size:11px; line-height:16px; padding:0 4px; color:var(--pl-ink); pointer-events:none}
/* Planner house style — own modern CSS, not Bootstrap 3.
   Design source of truth: planning/preproduction-planner-mockup.html
   NOTE: the shared chip/bar/avatar system + palette vars live in the
   UNSCOPED style block at the bottom of this file, because child
   components (DayView etc.) render those elements in their own
   templates where scoped rules can't reach. */
.pl-root{
    background:var(--pl-page); color:var(--pl-ink);
    font:15px/1.5 system-ui,-apple-system,"Segoe UI",sans-serif;
    margin:0 -15px; padding:16px 18px 40px; min-height:80vh;
}
.pl-root *{box-sizing:border-box}
.pl-root button{font:inherit; cursor:pointer}

/* ---- app bar ---- */
.pl-appbar{display:flex; align-items:center; gap:14px; flex-wrap:wrap;
    background:var(--pl-card); border:1px solid var(--pl-line); border-radius:14px;
    box-shadow:0 1px 2px rgba(11,11,11,.06); padding:10px 16px; margin-bottom:14px}
.pl-brand{display:flex; align-items:center; gap:9px; font-weight:700; font-size:17px}
.pl-livedot{width:10px; height:10px; border-radius:50%; background:var(--pl-good); box-shadow:0 0 0 3px var(--pl-good-soft)}
.pl-zoom{display:flex; background:var(--pl-card2); border-radius:10px; padding:3px; gap:2px}
.pl-zoom button{border:0; background:transparent; border-radius:8px; padding:8px 18px; font-weight:600; color:var(--pl-ink2)}
.pl-zoom button.on{background:var(--pl-ink); color:#fff}
.pl-zoom button:disabled{opacity:.45; cursor:not-allowed}
.pl-pager{display:flex; gap:4px}
.pl-pager button{border:1px solid var(--pl-line); background:var(--pl-card); border-radius:8px;
    min-width:40px; height:38px; font-size:16px; font-weight:600; color:var(--pl-ink2)}
.pl-today-btn{padding:0 14px}
.pl-range{font-weight:700; font-size:16px; white-space:nowrap}
.pl-filters{display:flex; gap:8px; margin-left:auto; align-items:center; flex-wrap:wrap}
.pl-filter{border:1px solid var(--pl-line); background:var(--pl-card); border-radius:8px;
    padding:8px 10px; color:var(--pl-ink); font-weight:600; font-size:14px; max-width:230px}
.pl-filter-clear{border:0; background:var(--pl-card2); border-radius:8px; padding:8px 10px; font-weight:700; font-size:13px; color:var(--pl-ink2)}
.pl-select-btn{border:1px solid var(--pl-line); background:var(--pl-card); border-radius:8px; padding:8px 12px;
    font-weight:700; font-size:13.5px; color:var(--pl-ink2); white-space:nowrap}
.pl-select-btn.on{background:var(--pl-ink); border-color:var(--pl-ink); color:#fff}

/* ---- selection bar (multi-select → bulk move) ---- */
.pl-selbar{position:sticky; top:8px; z-index:60; display:flex; align-items:center; gap:10px; flex-wrap:wrap;
    background:var(--pl-ink); color:#fff; border-radius:12px; padding:9px 14px; margin-bottom:14px;
    box-shadow:0 6px 18px rgba(11,11,11,.28)}
.pl-selbar-n{font-size:14px; font-weight:600}
.pl-selbar-n b{font-size:16px}
.pl-selbar-hint{color:rgba(255,255,255,.72)}
.pl-selbar-off{color:#f6d47a}
.pl-selbar-add{border:1px dashed rgba(255,255,255,.55); background:transparent; color:#fff; border-radius:8px;
    padding:6px 10px; font-weight:700; font-size:13px}
.pl-selbar-add:hover{background:rgba(255,255,255,.12)}
.pl-selbar-sp{flex:1}
.pl-selbar-move{border:0; background:var(--pl-good); color:#fff; border-radius:8px; padding:8px 16px; font-weight:800; font-size:14px}
.pl-selbar-move:disabled{opacity:.45; cursor:default}
.pl-selbar-btn{border:1px solid rgba(255,255,255,.4); background:transparent; color:#fff; border-radius:8px;
    padding:7px 12px; font-weight:700; font-size:13px}
.pl-selbar-btn:disabled{opacity:.45; cursor:default}

.pl-error{background:var(--pl-crit-soft); border:1px solid var(--pl-crit); color:#7c1d1d;
    border-radius:10px; padding:10px 14px; margin-bottom:12px; font-weight:600;
    display:flex; justify-content:space-between; gap:10px}
.pl-error button{border:0; background:transparent; font-weight:800; color:inherit}
.pl-loading{color:var(--pl-ink3); font-weight:600; padding:30px 0; text-align:center}

/* ---- jobs lane ---- */
.pl-lane{background:var(--pl-card); border:1px solid var(--pl-line); border-radius:14px;
    box-shadow:0 1px 2px rgba(11,11,11,.06); padding:12px 16px 14px; margin-bottom:14px}
.pl-lane-head{display:flex; align-items:center; gap:10px; margin-bottom:8px; flex-wrap:wrap}
.pl-lane-head h2{font-size:15px; font-weight:700; margin:0}
.pl-lane-count{color:var(--pl-ink3); font-size:13px; font-weight:600}
.pl-lane-legend{display:flex; gap:14px; font-size:12.5px; color:var(--pl-ink2); font-weight:600}
.pl-lane-legend span{display:flex; align-items:center; gap:5px}
.pl-lane-legend i{width:10px; height:10px; border-radius:3px; display:inline-block}
.pl-lane-fold{margin-left:auto; border:0; background:transparent; color:var(--pl-ink3); font-weight:600; font-size:13px}
.pl-lane-rows{display:flex; flex-direction:column; gap:5px}
.pl-lane-row{display:grid; grid-template-columns:210px 1fr 100px; align-items:center; gap:10px; min-height:26px}
.pl-lane-lbl{font-size:13px; font-weight:600; color:var(--pl-ink2); white-space:nowrap; overflow:hidden; text-overflow:ellipsis}
.pl-lane-lbl b{color:var(--pl-ink)}
.pl-lane-sheet{display:inline-block; text-decoration:none; font-size:13px; margin-right:5px;
    border:1px solid var(--pl-line); border-radius:6px; padding:0 4px; background:var(--pl-card2)}
.pl-lane-sheet:hover{border-color:var(--pl-ink3)}
.pl-lane-row-active .pl-lane-lbl, .pl-lane-row-active .pl-lane-lbl b{color:var(--pl-ink)}
.pl-lane-val{font-size:12.5px; color:var(--pl-ink3); text-align:right; font-weight:600; white-space:nowrap}
.pl-lane-track{position:relative; height:22px; background:var(--pl-card2); border-radius:6px}
.pl-lane-gridl{position:absolute; top:0; bottom:0; width:1px; background:var(--pl-line)}
.pl-lane-bar{position:absolute; top:2px; bottom:2px; border-radius:5px; display:flex; align-items:center; cursor:pointer}
.pl-lane-bar.pl-risk-ok{background:var(--pl-good-soft); outline:1.5px solid var(--pl-good)}
.pl-lane-bar.pl-risk-tight{background:var(--pl-warn-soft); outline:1.5px solid var(--pl-warn)}
.pl-lane-bar.pl-risk-late{background:var(--pl-crit-soft); outline:1.5px solid var(--pl-crit)}
.pl-lane-bar.pl-risk-feed{background:var(--pl-card2); outline:1.5px dashed var(--pl-ink3)}
.pl-lane-fill{position:absolute; left:0; top:0; bottom:0; border-radius:5px 0 0 5px; opacity:.5; display:block}
.pl-risk-ok .pl-lane-fill{background:var(--pl-good)}
.pl-risk-tight .pl-lane-fill{background:var(--pl-warn); opacity:.65}
.pl-risk-late .pl-lane-fill{background:var(--pl-crit)}
.pl-risk-feed .pl-lane-fill{background:var(--pl-ink3)}
.pl-lane-ghost{opacity:.6; outline-style:dashed !important}
.pl-lane-flag{position:absolute; top:-5px; bottom:-5px; width:3px; border-radius:2px; background:var(--pl-crit)}
.pl-lane-flag::after{content:"⚑"; position:absolute; top:-13px; left:-4px; font-size:11px; color:var(--pl-crit)}
.pl-lane-pct{position:relative; font-size:11.5px; font-weight:700; padding-left:7px; white-space:nowrap}
.pl-cont-r{border-top-right-radius:0; border-bottom-right-radius:0}
.pl-cont-l{border-top-left-radius:0; border-bottom-left-radius:0}

/* ---- board ---- */
.pl-board{display:grid; grid-template-columns:232px 1fr; gap:14px; align-items:start}
.pl-board > .pl-gridwrap:only-child{grid-column:1 / -1}
.pl-tray{background:var(--pl-card); border:1px solid var(--pl-line); border-radius:14px;
    box-shadow:0 1px 2px rgba(11,11,11,.06); padding:12px}
.pl-tray h2{font-size:14px; font-weight:700; display:flex; align-items:center; gap:8px; margin:0 0 2px}
.pl-tray-sub{font-size:12.5px; color:var(--pl-ink3); margin-bottom:10px}
.pl-tray-q{border:1px solid var(--pl-line); border-radius:10px; padding:9px 10px; margin-bottom:9px; background:var(--pl-page)}
.pl-tray-qh{display:flex; align-items:baseline; gap:6px; margin-bottom:6px}
.pl-tray-qh b{font-size:13.5px}
.pl-tray-qh span{font-size:12px; color:var(--pl-ink3); font-weight:600; margin-left:auto}
.pl-tray-list{display:flex; flex-direction:column; gap:4px; min-height:10px}

.pl-gridwrap{overflow-x:auto}
.pl-grid{display:grid; min-width:900px;
    background:var(--pl-card); border:1px solid var(--pl-line); border-radius:14px;
    box-shadow:0 1px 2px rgba(11,11,11,.06); overflow:hidden}
.pl-grid > div{border-bottom:1px solid var(--pl-line); border-right:1px solid var(--pl-line); min-width:0}
.pl-hd{background:var(--pl-card2); padding:9px 12px; font-weight:700; font-size:13.5px;
    display:flex; align-items:center; gap:8px}
.pl-hd-num{color:var(--pl-ink3); font-weight:600}
.pl-hd-today{background:var(--pl-ink); color:#fff}
.pl-hd-today .pl-hd-num{color:#c9c8c0}
.pl-today-pill{margin-left:auto; background:#fff; color:var(--pl-ink); font-size:11px; border-radius:20px; padding:1px 8px; font-weight:800}
.pl-hd-weekend{opacity:.6}

.pl-person{padding:12px; display:flex; flex-wrap:wrap; align-items:flex-start; gap:10px; background:var(--pl-card2); position:relative}
.pl-person-tx{flex:1 1 auto; min-width:0}
.pl-limit-wrap{flex:0 0 100%; position:relative}
/* .pl-av / .pl-person-nm are in the shared unscoped block below */
.pl-limit-pill{display:inline-flex; align-items:center; gap:4px; font-size:11px; color:var(--pl-ink2); font-weight:700;
    max-width:100%; white-space:nowrap; border:1px solid var(--pl-line); background:var(--pl-card);
    border-radius:20px; padding:2.5px 8px}
.pl-limit-pill:disabled{cursor:default}
.pl-limit-pill .pl-i{width:10px; height:10px}
.pl-pen{color:var(--pl-ink3)}

.pl-limpop{position:absolute; z-index:30; top:calc(100% + 4px); left:0; width:230px;
    background:var(--pl-card); border:1.5px solid var(--pl-ink); border-radius:10px;
    box-shadow:0 8px 22px rgba(11,11,11,.16); padding:11px 12px}
.pl-limpop h5{font-size:11.5px; letter-spacing:.05em; text-transform:uppercase; color:var(--pl-ink3); font-weight:800; margin:0 0 8px}
.pl-limpop-step{display:flex; align-items:center; gap:10px; margin-bottom:8px}
.pl-limpop-step button{width:38px; height:38px; border:1px solid var(--pl-line); background:var(--pl-page);
    border-radius:9px; font-size:19px; font-weight:800; color:var(--pl-ink)}
.pl-limpop-step b{font-size:21px; font-weight:800; flex:1; text-align:center}
.pl-limpop p{font-size:12px; color:var(--pl-ink2); line-height:1.4; margin:0 0 9px}
.pl-limpop-btns{display:flex; gap:7px}
.pl-limpop-save{flex:1; border:0; background:var(--pl-ink); color:#fff; border-radius:9px; padding:9px; font-weight:800; font-size:13.5px}
.pl-limpop-cancel{border:1px solid var(--pl-line); background:var(--pl-card); border-radius:9px; padding:9px 12px; font-weight:700; font-size:13px; color:var(--pl-ink2)}
.pl-hrs-row{display:flex; align-items:center; gap:8px; margin-bottom:7px}
.pl-hrs-row label{flex:0 0 44px; font-size:12px; font-weight:700; color:var(--pl-ink2)}
.pl-hrs-row input{flex:1; min-width:0; border:1px solid var(--pl-line); border-radius:8px; padding:7px 8px;
    font:inherit; font-size:13px; background:var(--pl-page)}
.pl-hrs-off{border:1px solid var(--pl-line); background:var(--pl-card); border-radius:8px; padding:7px 10px;
    font-weight:800; font-size:12px; cursor:pointer; font-family:inherit}
.pl-hrs-clear{display:block; width:100%; margin-top:7px; border:0; background:none; color:var(--pl-ink2);
    font-size:12px; font-weight:700; text-decoration:underline; cursor:pointer; font-family:inherit; padding:2px}

.pl-cell{padding:8px; min-height:118px; display:flex; flex-direction:column; gap:5px; background:var(--pl-card)}
.pl-cell-today{background:linear-gradient(rgba(11,11,11,.025),rgba(11,11,11,.025))}
/* past days: still droppable (back-dating is allowed), just visibly behind us */
.pl-cell-past{background:linear-gradient(rgba(11,11,11,.045),rgba(11,11,11,.045))}
.pl-cell-weekend{background:repeating-linear-gradient(45deg,var(--pl-card2) 0 8px,var(--pl-card) 8px 16px)}
.pl-cell-invalid{background:repeating-linear-gradient(45deg,#efeeea 0 7px,#f6f5f2 7px 14px)}
.pl-cell-invalid .pl-cell-list{opacity:.5}
.pl-cell-list{display:flex; flex-direction:column; gap:5px; flex:1; min-height:26px}
.pl-cell-why{display:flex; align-items:center; gap:6px; color:var(--pl-ink3); font-size:11.5px; font-weight:700; margin-top:auto}
.pl-cell-why .pl-i{width:13px; height:13px}
.pl-row-dim{opacity:.45}

.pl-wktotal{padding:10px; display:flex; flex-direction:column; gap:6px; justify-content:center; background:var(--pl-card2)}
.pl-wktotal-t{font-size:12.5px; font-weight:700; white-space:nowrap}
.pl-wktotal-t small{color:var(--pl-ink3); font-weight:600}
.pl-wktotal .pl-cap-n small{display:none}

/* ---- capacity bars ---- */
/* capacity bars + chips + pills are in the shared unscoped block below */

/* sortable ghost while dragging */
.pl-chip.sortable-ghost{opacity:.4; outline:2px dashed var(--plwc, var(--pl-ink3)); outline-offset:1px}

/* ---- assign mode ---- */
.pl-assign-banner{display:flex; align-items:center; gap:16px; flex-wrap:wrap;
    background:var(--pl-ink); color:#fff; border-radius:14px;
    padding:12px 18px; margin-bottom:14px; box-shadow:0 1px 2px rgba(11,11,11,.06)}
.pl-assign-t{font-weight:800; font-size:16px}
.pl-assign-sub{color:#c9c8c0; font-size:13px; font-weight:600}
.pl-assign-prog{display:flex; align-items:center; gap:8px; font-weight:700; font-size:13.5px}
.pl-assign-bar{width:130px; height:8px; border-radius:4px; background:#3a3a36; overflow:hidden; display:inline-block}
.pl-assign-bar i{display:block; height:100%; background:var(--pl-good); border-radius:4px}
.pl-assign-done{margin-left:auto; background:#fff; color:var(--pl-ink); border:0; border-radius:9px;
    font-weight:800; padding:9px 16px; font-size:14px}
.pl-shelf{background:var(--pl-card); border:1px solid var(--pl-line); border-radius:14px;
    box-shadow:0 1px 2px rgba(11,11,11,.06); padding:12px 14px; margin-bottom:14px}
.pl-shelf-empty{color:var(--pl-ink2); font-weight:650}
.pl-tiles{display:flex; gap:10px; flex-wrap:wrap}
.pl-tile{display:flex; align-items:center; gap:10px;
    border:1px solid var(--pl-line); border-left:6px solid var(--plwc, var(--pl-ink3));
    background:var(--pl-card); border-radius:10px; padding:10px 12px;
    box-shadow:0 1px 2px rgba(11,11,11,.06); cursor:grab; min-height:56px; user-select:none}
.pl-tile-i{width:22px; height:22px; color:var(--plwc, var(--pl-ink3))}
.pl-tile-nm{font-weight:700; font-size:14px; line-height:1.25}
.pl-tile-meta{font-size:12px; color:var(--pl-ink3); font-weight:600}
/* which unit of a multi-item order this process belongs to (carries the
   client's ref code) — same tier of prominence as the meta line */
.pl-tile-item{font-size:12px; color:var(--pl-ink2); font-weight:650; overflow:hidden;
    text-overflow:ellipsis; white-space:nowrap; max-width:240px}
.pl-tile-auto{display:flex; align-items:center; gap:5px;
    border:1.5px solid var(--pl-ink); background:var(--pl-card); color:var(--pl-ink);
    border-radius:8px; padding:7px 11px; font-weight:800; font-size:12.5px; margin-left:6px}
.pl-tile-auto .pl-i{width:13px; height:13px}
.pl-tile-auto:disabled{opacity:.6}
.pl-tile.sortable-ghost{opacity:.4; outline:2px dashed var(--plwc, var(--pl-ink3))}
.pl-shiftctl{display:flex; gap:5px; align-items:center}
.pl-shift-days{width:60px; border:1px solid var(--pl-line); border-radius:8px; padding:8px 8px; font:inherit; font-weight:700; text-align:center}
.pl-jobsearch{position:relative; display:inline-block}
.pl-jobsearch-in{min-width:240px}
.pl-jobsearch-dd{position:absolute; top:calc(100% + 4px); left:0; z-index:60; min-width:280px;
    background:var(--pl-card); border:1px solid var(--pl-line); border-radius:10px;
    box-shadow:0 8px 24px rgba(11,11,11,.14); overflow:hidden}
.pl-jobsearch-dd button{display:block; width:100%; text-align:left; border:0; background:transparent;
    padding:9px 13px; font:inherit; font-size:13.5px; cursor:pointer}
.pl-jobsearch-dd button:hover{background:var(--pl-card2)}
.pl-jobsearch-dd button b{color:var(--pl-ink)}

/* ---- drop receipt toast ---- */
.pl-toast{position:fixed; left:50%; bottom:26px; transform:translateX(-50%); z-index:90;
    background:var(--pl-ink); color:#fff; border-radius:12px; padding:10px 16px;
    display:flex; align-items:center; gap:12px; font-size:13.5px; font-weight:650;
    box-shadow:0 6px 24px rgba(11,11,11,.35); max-width:min(92vw, 560px)}
.pl-toast-undo{border:1px solid rgba(255,255,255,.4); background:transparent; color:#fff;
    border-radius:8px; padding:5px 12px; font-weight:800; font-size:12.5px; flex:none}
.pl-toast-undo:hover{background:rgba(255,255,255,.12)}
.pl-toast-done{border-color:var(--pl-good); color:#b6f0b6}
.pl-toast-x{border:0; background:transparent; color:rgba(255,255,255,.7); font-weight:800; flex:none; padding:2px 4px}
.pl-ddl-flag{font-size:14px; color:var(--pl-ink2)}
.pl-ddl-date{border:1px solid var(--pl-line); border-radius:8px; padding:7px 8px; font:inherit; font-weight:650; font-size:13px; background:var(--pl-card)}
.pl-shiftctl button, .pl-adhoc-btn{border:1px solid var(--pl-ink); background:var(--pl-card); color:var(--pl-ink);
    border-radius:8px; padding:8px 12px; font-weight:800; font-size:13px}
.pl-adhoc-form{display:flex; gap:8px; align-items:center; flex-wrap:wrap; width:100%;
    border-top:1px solid var(--pl-line); margin-top:10px; padding-top:10px}
.pl-adhoc-title{flex:1; min-width:220px}
.pl-adhoc-mins{width:80px}
.pl-adhoc-add{border:0; background:var(--pl-ink); color:#fff; border-radius:8px; padding:9px 14px; font-weight:800; font-size:13.5px}
.pl-adhoc-add:disabled{opacity:.5}
.pl-feed-form{display:flex; gap:8px; align-items:center; flex-wrap:wrap; width:100%;
    margin-top:8px; padding-top:10px; border-top:1px dashed var(--pl-line)}
.pl-feed-lbl{display:inline-flex; align-items:center; gap:5px; font-weight:800; font-size:13px; color:var(--pl-ink)}
.pl-feed-lbl .pl-i{width:13px; height:13px}
.pl-feed-rate{width:60px; text-align:center; font-weight:700}
.pl-feed-unit{font-size:12.5px; color:var(--pl-ink2); font-weight:600}
.pl-feed-go{border:0; background:var(--pl-ink); color:#fff; border-radius:8px; padding:9px 14px; font-weight:800; font-size:13px}
.pl-feed-go:disabled{opacity:.4}

.pl-tray-go{display:block; width:100%; margin-top:7px;
    font-size:12.5px; font-weight:700; color:var(--pl-ink);
    background:var(--pl-card2); border:1px solid var(--pl-line); border-radius:7px; padding:6px 9px}

@media (max-width:1100px){
    .pl-board{grid-template-columns:1fr}
}
</style>

<style>
/* ============================================================
   Shared planner design system — UNSCOPED on purpose.
   DayView (and any other child component) renders chips, capacity
   bars and avatars in its own template, where PlannerPage's scoped
   rules can't reach (different data-v attribute). Everything here
   is pl- prefixed, so it can't collide with the rest of the app.
   Palette matches planning/preproduction-planner-mockup.html.
   ============================================================ */
.pl-root{
    --pl-page:#f9f9f7; --pl-card:#fcfcfb; --pl-card2:#f4f3f0;
    --pl-ink:#0b0b0b; --pl-ink2:#52514e; --pl-ink3:#898781;
    --pl-line:#e1e0d9;
    --pl-good:#0ca30c; --pl-warn:#fab219; --pl-crit:#d03b3b;
    --pl-good-soft:#e3f4e3; --pl-warn-soft:#fdf3d9; --pl-crit-soft:#f9e4e4;
}
.pl-i{width:16px; height:16px; flex:none; display:block}
.pl-av{width:38px; height:38px; border-radius:50%; background:var(--pl-ink); color:#fff;
    display:flex; align-items:center; justify-content:center; font-weight:800; font-size:14px; flex:none}
.pl-person-nm{font-weight:700; font-size:14.5px; line-height:1.25}

/* ---- capacity bars ---- */
.pl-cap{display:flex; align-items:center; gap:7px}
.pl-cap-track{flex:1; height:7px; background:var(--pl-card2); border-radius:4px; overflow:hidden; position:relative}
.pl-cap-fill{display:block; height:100%; border-radius:4px}
.pl-b-low  .pl-cap-fill{background:var(--pl-good); opacity:.4}
.pl-b-ok   .pl-cap-fill{background:var(--pl-good)}
.pl-b-tight .pl-cap-fill{background:var(--pl-warn)}
.pl-b-over .pl-cap-fill{background:var(--pl-crit)}
.pl-cap-lim{position:absolute; top:0; bottom:0; width:2.5px; background:var(--pl-ink); opacity:.65; border-radius:1px; transform:translateX(-50%)}
.pl-cap-n{font-size:11.5px; font-weight:700; color:var(--pl-ink2); white-space:nowrap}
.pl-b-over .pl-cap-n{color:var(--pl-crit)}
.pl-b-tight .pl-cap-n{color:#8a6100}
.pl-cap-n small{color:var(--pl-ink3); font-weight:600}
.pl-cap-none{color:var(--pl-ink3); font-weight:600}

/* ---- chips ---- */
.pl-chip{display:flex; align-items:flex-start; gap:7px;
    background:var(--pl-card); border:1px solid var(--pl-line); border-left:5px solid var(--plwc, var(--pl-ink3));
    border-radius:8px; padding:6px 8px 6px 7px; box-shadow:0 1px 1.5px rgba(11,11,11,.05);
    font-size:13px; line-height:1.3; cursor:grab; min-height:34px; user-select:none}
.pl-chip .pl-i{margin-top:2px; color:var(--plwc, var(--pl-ink3))}
.pl-chip-cx{flex:1; min-width:0}
.pl-chip-r1{display:flex; align-items:baseline; gap:7px}
.pl-chip-nm{font-weight:650; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; min-width:0; flex:1}
.pl-chip-mt{color:var(--pl-ink2); font-size:11.5px; font-weight:700; white-space:nowrap; flex:none}
.pl-chip-r2{display:flex; align-items:center; gap:6px; margin-top:1px; min-width:0}
.pl-chip-item{font-size:11px; color:var(--pl-ink2); font-weight:650; overflow:hidden;
    text-overflow:ellipsis; white-space:nowrap; min-width:0}
.pl-chip-cq{color:var(--pl-ink3); font-size:11px; font-weight:600; white-space:nowrap; overflow:hidden; text-overflow:ellipsis}
.pl-chip-done{opacity:.5; cursor:default}
.pl-chip-ghost{background:repeating-linear-gradient(135deg, var(--pl-card) 0 6px, var(--pl-card2) 6px 12px);
    color:var(--pl-ink2); border-style:dashed; border-left-style:solid}
.pl-chip-typed{border-style:dashed; border-left-style:solid; background:var(--pl-page)}
.pl-chip-sib{outline:2px solid var(--pl-ink); outline-offset:1px}
.pl-no-drag{cursor:default}

/* ---- multi-select state (Week + Day; the badge sits on the chip's corner) ---- */
.pl-chip{position:relative}
.pl-selecting .pl-chip{cursor:pointer}
.pl-chip-ck{position:absolute; top:-7px; right:-7px; width:19px; height:19px; border-radius:50%;
    border:2px solid var(--pl-ink3); background:var(--pl-card); color:#fff; font-size:12px; font-weight:900;
    line-height:15px; text-align:center; box-shadow:0 1px 2px rgba(11,11,11,.18); z-index:1}
.pl-chip-sel{outline:2px solid var(--pl-good); outline-offset:1px; background:var(--pl-good-soft)}
.pl-chip-sel .pl-chip-ck{background:var(--pl-good); border-color:var(--pl-good)}
.pl-chip-unsel{opacity:.4}
.pl-selecting .pl-chip-unsel{cursor:not-allowed}

/* ---- drag feel (forceFallback mode) ---- */
/* the clone in hand rides on <body> (fallbackOnBody), outside .pl-root,
   so the palette vars it uses have to be re-declared on it */
.pl-chip.sortable-fallback, .pl-tile.sortable-fallback{
    --pl-page:#f9f9f7; --pl-card:#fcfcfb; --pl-card2:#f4f3f0;
    --pl-ink:#0b0b0b; --pl-ink2:#52514e; --pl-ink3:#898781;
    --pl-line:#e1e0d9; --pl-warn-soft:#fdf3d9;
    opacity:.95 !important; z-index:99999; pointer-events:none;
    box-shadow:0 12px 28px rgba(11,11,11,.3); transform:rotate(1.5deg)}
.pl-chip.sortable-chosen{box-shadow:0 3px 10px rgba(11,11,11,.2); cursor:grabbing}
/* the cell / person that will receive the chip when released */
.pl-cell-target{outline:2px solid var(--pl-good); outline-offset:-2px; background:var(--pl-good-soft) !important}
.pl-row-target{outline:2px solid var(--pl-good); outline-offset:-2px; background:var(--pl-good-soft)}
.pl-pill{font-size:10px; font-weight:800; letter-spacing:.04em; border-radius:20px; padding:1px 7px; flex:none}
.pl-pill-draft{background:var(--pl-card2); color:var(--pl-ink2); border:1px dashed var(--pl-ink3)}
.pl-pill-seq{background:var(--pl-warn-soft); color:#8a6100}
</style>
